// 自動生成。編集しないこと。

import fs from '../shims/fs.mjs';
import path from '../shims/path.mjs';
export function generateTreasuresInVfs(rootDir) {
const ROOT_DIR = path.resolve(rootDir);
const MAPS_DIR = path.join(ROOT_DIR, 'maps');
const SHARED_DIR = path.join(MAPS_DIR, '_shared');
const MAP_LIST_PATH = path.join(SHARED_DIR, 'map-list.json');
const ITEMS_PATH = path.join(SHARED_DIR, 'items.json');
const SHOP_MASTER_PATH = path.join(SHARED_DIR, 'shop-master.json');
const ITEM_CATEGORIES_PATH = path.join(SHARED_DIR, 'item-categories.json');

const MAX_BLOCK_Y = 127;
// 建造物の外に置く宝箱の目標数。建造物内の宝箱は必ず別枠で追加し、その分だけ屋外枠を減らす
// （建物が多いマップで総数が膨らみ過ぎないようにするが、下限は必ず残す）。
const OVERWORLD_OUTDOOR_COUNT = 30;
const OVERWORLD_OUTDOOR_MIN_COUNT = 12;
const FLOOR_OUTDOOR_COUNT = 10;
const FLOOR_OUTDOOR_MIN_COUNT = 4;
const TREASURE_TYPES = ['copper', 'silver', 'gold'];
// 宝箱ランクの配分（2026-08-02 指示）。ワールド（マップ）単位でこの比率になるよう割り当てる。
const TREASURE_TYPE_RATIO_DEFAULTS = { copper: 0.8, silver: 0.15, gold: 0.05 };
// 中身の重複を避けるランク。銅は安価な消耗品前提なので重複してよい。
const DISTINCT_REWARD_TIER_DEFAULTS = ['silver', 'gold'];
// 同一アイテムを複数個入れてよいランク。基本は 1 個で、まとめ入りは金だけ（2026-08-02 指示）。
const STACKABLE_REWARD_TIER_DEFAULTS = ['gold'];
// 宝箱の抽選対象にしてよい「販売枠」。ここに無い枠（イベント限定販売・日次配布・
// デバッグ配布・交換所・宝物庫報酬）でしか手に入らない貴重品は宝箱へ入れない。
const TREASURE_SALE_CHANNEL_DEFAULTS = ['commonPools', 'exclusiveEntries', 'midnightEntries', 'blackmarketEntries'];
const SHOP_SALE_ENTRY_KEYS = ['exclusiveEntries', 'midnightEntries', 'blackmarketEntries', 'eventLimitedEntries'];
// 宝箱へ入れないアイテムカテゴリ（item-categories.json のカテゴリ ID）。
// 宝物庫＝宝物庫の攻略でしか手に入らない品、ペット＝課金プレゼント専用。
const EXCLUDED_ITEM_CATEGORY_DEFAULTS = ['vault', 'pet'];
// 価値スコアの重み。「スポーンからの水平距離」と「スポーンより高い位置」を、
// それぞれマップ内の最大値で正規化してから足す。ワールドの広さに依存せず、
// 塔や天守の最上階が世界の果てと同格に評価されるよう既定は 1:1。
const VALUE_RANKING_DEFAULTS = { distanceWeight: 1, heightWeight: 1 };
// 建造物内の宝箱配置の既定値（shop-master.json の randomTreasurePolicy.buildingInterior で上書き可）。
const BUILDING_INTERIOR_DEFAULTS = {
    volumePerChest: 900,
    maxChestsPerStructure: 10,
    maxChestsPerFloor: 3,
    minChestSeparation: 4
};
// 報酬アイテムの価格帯（ショップ購入価格）。銅 = 1,000G 以下 / 銀 = その上〜金未満 / 金 = 10,000G 以上。
const REWARD_TIER_DEFAULTS = {
    copperMaxBuyPrice: 1000,
    goldMinBuyPrice: 10000
};
const HAZARD_SURFACE_BLOCKS = new Set(['WATER', 'POISON', 'MAGMA']);
const RUIN_KEYWORDS = /(ruin|ruins|collapsed|abandoned|wreck|chapel_ruins|observation_ruins)/i;
const MAJOR_STRUCTURE_KEYWORDS = /(castle|keep|tower|watchtower|barracks|chapel|church|cathedral|sanct|temple|throne|hall|station|observatory|fort|ark|inn|manor|palace|altar)/i;
const BUILDING_OFFSETS_MAJOR = [
    [0, 0],
    [3, 0],
    [-3, 0],
    [0, 3],
    [0, -3],
    [4, 4],
    [-4, 4],
    [4, -4],
    [-4, -4],
    [6, 0],
    [0, 6]
];

const BUILDING_OFFSETS_SMALL = [
    [0, 0],
    [2, 0],
    [-2, 0],
    [0, 2],
    [0, -2],
    [3, 3],
    [-3, 3],
    [3, -3]
];

const ENEMY_OFFSETS = [
    [3, 0],
    [-3, 0],
    [0, 3],
    [0, -3],
    [4, 2],
    [-4, 2],
    [2, 4],
    [-2, -4]
];

const ENEMY_CLUSTER_ANCHOR_OFFSETS = [
    [10, 0],
    [-10, 0],
    [0, 10],
    [0, -10],
    [12, 8],
    [-12, 8],
    [12, -8],
    [-12, -8]
];

function readJson(filePath) {
    return JSON.parse(fs.readFileSync(filePath, 'utf8'));
}

function writeJson(filePath, value) {
    fs.writeFileSync(filePath, `${JSON.stringify(value, null, 2)}\n`, 'utf8');
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function hashString(input) {
    let hash = 2166136261;
    const text = String(input || '');
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function createRng(seedInput) {
    let state = hashString(seedInput) || 1;
    return () => {
        state += 0x6D2B79F5;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function pickRandomInt(rng, min, max) {
    const safeMin = Math.min(min, max);
    const safeMax = Math.max(min, max);
    return Math.floor(rng() * (safeMax - safeMin + 1)) + safeMin;
}

/**
 * 宝箱の抽選対象になる「ショップ販売価格」を集める（2026-08-02 指示）。
 *
 * 読むのは shop-master の commonPools と shops.json の販売枠のうち、
 * policy.treasureSaleChannels で許可されたものだけ。
 * dailyGrants / debugStartupGrants / 交換所 / 宝物庫報酬（バフ鉱石など）といった
 * 「特別なイベントでしか手に入らない」配布チャンネルは走査しないため、
 * そこ由来の貴重品が宝箱へ混ざることはない。
 */
function collectShopSaleEntries(entries, prices) {
    if (!Array.isArray(entries)) return;
    for (const entry of entries) {
        if (!entry || typeof entry !== 'object') continue;
        const itemId = String(entry.itemId || '').trim();
        const buyPrice = Number(entry.buyPrice);
        if (!itemId || !Number.isFinite(buyPrice) || buyPrice <= 0) continue;
        const previous = prices.get(itemId);
        if (!Number.isFinite(previous) || buyPrice < previous) prices.set(itemId, buyPrice);
    }
}

function collectShopPurchasePrices(document, prices, channels) {
    if (!document || typeof document !== 'object') return;

    if (channels.has('commonPools')) {
        const pools = document.commonPools;
        if (Array.isArray(pools)) {
            for (const pool of pools) collectShopSaleEntries(pool?.entries, prices);
        } else if (pools && typeof pools === 'object') {
            for (const entries of Object.values(pools)) collectShopSaleEntries(entries, prices);
        }
    }

    for (const profile of Array.isArray(document.shopProfiles) ? document.shopProfiles : []) {
        for (const key of SHOP_SALE_ENTRY_KEYS) {
            if (channels.has(key)) collectShopSaleEntries(profile?.[key], prices);
        }
    }
}

/** 宝箱の抽選対象にしてよい販売枠（既定はイベント限定販売を除く通常の販売枠）。 */
function resolveTreasureSaleChannels(policy) {
    const raw = policy?.treasureSaleChannels;
    if (!Array.isArray(raw)) return new Set(TREASURE_SALE_CHANNEL_DEFAULTS);
    const channels = raw.map((value) => String(value || '').trim()).filter(Boolean);
    return new Set(channels.length > 0 ? channels : TREASURE_SALE_CHANNEL_DEFAULTS);
}

function isForbiddenRandomTreasureItem(item) {
    const itemId = String(item?.id || '').trim();
    const effect = String(item?.effect || '').trim();
    const type = String(item?.type || '').trim();
    const category = String(item?.category || '').trim();
    if (item?.shopForbidden === true) return true;
    if (itemId.startsWith('pet_')
        || type === 'pet'
        || category === 'pet'
        || effect === 'buff_summon_pet'
        || String(item?.summonPetId || '').trim()
        || String(item?.petId || '').trim()) return true;
    return /^(?:teleport_(?:dungeon|labyrinth|maze)(?:_|$)|start_eternal_vault$)/.test(effect);
}

/**
 * item-categories.json のカテゴリ matcher（アイテム一覧タブと同じ判定）を再現する。
 * 宝物庫・ペットのようなカテゴリを宝箱の抽選から丸ごと外すために使う。
 */
function itemMatchesCategoryMatcher(item, matcher) {
    if (!matcher || typeof matcher !== 'object') return false;
    const itemId = String(item?.id || '').trim();
    const effect = String(item?.effect || '').trim();
    const type = String(item?.type || '').trim();
    const list = (value) => (Array.isArray(value) ? value.map(String).filter(Boolean) : []);

    if (list(matcher.excludeItemIds).includes(itemId)) return false;
    if (list(matcher.excludeItemIdPrefixes).some((prefix) => itemId.startsWith(prefix))) return false;
    if (effect && list(matcher.effectPrefixes).some((prefix) => effect.startsWith(prefix))) return true;
    if (effect && list(matcher.effects).includes(effect)) return true;
    if (type && list(matcher.types).includes(type)) return true;
    if (list(matcher.itemIdPrefixes).some((prefix) => itemId.startsWith(prefix))) return true;
    return list(matcher.itemIds).includes(itemId);
}

/** 宝箱へ入れないカテゴリの matcher 一覧（既定は宝物庫・ペット）。 */
function resolveExcludedCategoryMatchers(itemCategories, policy) {
    const raw = policy?.excludeItemCategories;
    const excludedIds = new Set(Array.isArray(raw)
        ? raw.map((value) => String(value || '').trim()).filter(Boolean)
        : EXCLUDED_ITEM_CATEGORY_DEFAULTS);
    const categories = Array.isArray(itemCategories?.categories) ? itemCategories.categories : [];
    return categories
        .filter((category) => excludedIds.has(String(category?.id || '').trim()))
        .map((category) => category?.matcher)
        .filter(Boolean);
}

/**
 * 宝箱の抽選カタログを作る。
 * 大原則: **アイテムショップで販売しているアイテムだけ**を対象にする（2026-08-02 指示）。
 * 販売価格が取れない品（宝物庫報酬・交換所・配布専用・イベント限定販売）は入り口で落ち、
 * さらに宝物庫／ペットのカテゴリはカテゴリ定義ごと除外する。
 */
function buildRewardCatalog(items, shopDocuments, policy = {}, itemCategories = null) {
    const purchasePrices = new Map();
    const saleChannels = resolveTreasureSaleChannels(policy);
    for (const document of shopDocuments) collectShopPurchasePrices(document, purchasePrices, saleChannels);
    // 金ランクは 10,000G 以上のアイテムを使うため、上限は既定で 100,000G まで許す
    const maxBuyPrice = Number.isFinite(Number(policy.maxBuyPrice))
        ? Math.max(1, Number(policy.maxBuyPrice))
        : 100000;
    const excludedPrefixes = Array.isArray(policy.excludeItemIdPrefixes)
        ? policy.excludeItemIdPrefixes.map(String).filter(Boolean)
        : ['blueprint_'];
    const excludedEffects = new Set(Array.isArray(policy.excludeEffects)
        ? policy.excludeEffects.map(String)
        : ['place_blueprint', 'open_blueprint_selector']);
    const excludedCategoryMatchers = resolveExcludedCategoryMatchers(itemCategories, policy);

    return items
        .map((item) => {
            const itemId = String(item?.id || '').trim();
            const buyPrice = purchasePrices.get(itemId);
            if (!itemId) return null;
            if (isForbiddenRandomTreasureItem(item)) return null;
            if (excludedPrefixes.some((prefix) => itemId.startsWith(prefix))) return null;
            if (excludedEffects.has(String(item?.effect || ''))) return null;
            if (excludedCategoryMatchers.some((matcher) => itemMatchesCategoryMatcher(item, matcher))) return null;
            if (!Number.isFinite(buyPrice) || buyPrice <= 0 || buyPrice > maxBuyPrice) return null;

            return {
                ...item,
                itemId,
                buyPrice
            };
        })
        .filter(Boolean);
}

function getWorldSize(meta) {
    const size = meta?.world?.size || {};
    const width = Number(size.width ?? size.x);
    const depth = Number(size.depth ?? size.z);
    return {
        width: Number.isFinite(width) && width > 0 ? Math.floor(width) : 1008,
        depth: Number.isFinite(depth) && depth > 0 ? Math.floor(depth) : 1008
    };
}

function resolveReferencePosition(meta, terrain, refType) {
    const worldSize = getWorldSize(meta);
    const wc = terrain?.worldCenter || {};
    const wcX = Number.isFinite(Number(wc.x)) ? Number(wc.x) : Math.floor(worldSize.width / 2);
    const wcZ = Number.isFinite(Number(wc.z)) ? Number(wc.z) : Math.floor(worldSize.depth / 2);

    switch (String(refType || '').trim()) {
        case 'origin':
        case 'absolute':
        case 'world_origin':
            return { x: 0, z: 0 };
        case 'castle':
            return {
                x: wcX,
                z: Number.isFinite(Number(terrain?.castle?.positionZ)) ? Number(terrain.castle.positionZ) : wcZ
            };
        case 'player_spawn':
        case 'spawn':
            return {
                x: Number.isFinite(Number(meta?.player?.spawnPos?.[0])) ? Number(meta.player.spawnPos[0]) : wcX,
                z: Number.isFinite(Number(meta?.player?.spawnPos?.[2])) ? Number(meta.player.spawnPos[2]) : wcZ
            };
        case 'labyrinth':
            return {
                x: Number.isFinite(Number(terrain?.labyrinth?.entrancePos?.[0])) ? Number(terrain.labyrinth.entrancePos[0]) : wcX,
                z: Number.isFinite(Number(terrain?.labyrinth?.entrancePos?.[1])) ? Number(terrain.labyrinth.entrancePos[1]) : wcZ
            };
        case 'world_center':
        default:
            return { x: wcX, z: wcZ };
    }
}

function getTerrainHeight(meta, terrain, worldX, worldZ) {
    const generator = String(terrain?.generator || 'overworld');
    if (generator === 'dungeon') {
        return getDungeonFloorGroundY(terrain, 1);
    }
    if (generator === 'void') {
        const groundLevel = Number(terrain?.elevation?.groundLevel);
        return Math.max(0, Math.floor(Number.isFinite(groundLevel) ? groundLevel : 0));
    }

    const worldSize = getWorldSize(meta);
    const wc = terrain?.worldCenter || {};
    const worldCenterX = Number.isFinite(Number(wc.x)) ? Number(wc.x) : Math.floor(worldSize.width / 2);
    const worldCenterZ = Number.isFinite(Number(wc.z)) ? Number(wc.z) : Math.floor(worldSize.depth / 2);
    const boundaries = terrain?.boundaries || {};
    const elevation = terrain?.elevation || {};
    const stairs = Array.isArray(terrain?.stairs) ? terrain.stairs : [];
    const boundaryLowerUpper = Number.isFinite(Number(boundaries.lowerUpperDivide)) ? Number(boundaries.lowerUpperDivide) : worldCenterZ;
    const boundaryCastleUpper = Number.isFinite(Number(boundaries.castleUpperDivide)) ? Number(boundaries.castleUpperDivide) : 120;
    const lowerCityHeight = Number.isFinite(Number(elevation.lowerCityHeight)) ? Number(elevation.lowerCityHeight) : 9;
    const upperCityHeight = Number.isFinite(Number(elevation.upperCityHeight)) ? Number(elevation.upperCityHeight) : 12;
    const castleBaseHeight = Number.isFinite(Number(elevation.castleBaseHeight)) ? Number(elevation.castleBaseHeight) : 17;
    const seed = Number.isFinite(Number(terrain?.seed)) ? Number(terrain.seed) : 12345;
    const noiseScale = Number.isFinite(Number(terrain?.noise?.scale)) ? Number(terrain.noise.scale) : 0.05;
    const noiseAmplitude = Number.isFinite(Number(terrain?.noise?.amplitude)) ? Number(terrain.noise.amplitude) : 0.5;

    let stairHeight = null;
    for (const stair of stairs) {
        const zRange = Array.isArray(stair?.zRange) ? stair.zRange : [];
        const zMin = Number(zRange[0]);
        const zMax = Number(zRange[1]);
        if (!Number.isFinite(zMin) || !Number.isFinite(zMax)) continue;
        if (worldZ < zMin || worldZ > zMax) continue;

        const widthX = Number.isFinite(Number(stair.widthX)) ? Number(stair.widthX) : worldSize.width;
        const halfW = widthX / 2;
        if (Math.abs(worldX - worldCenterX) > halfW) continue;

        const fromHeight = Number.isFinite(Number(stair.fromHeight)) ? Number(stair.fromHeight) : lowerCityHeight;
        const toHeight = Number.isFinite(Number(stair.toHeight)) ? Number(stair.toHeight) : upperCityHeight;
        const span = Math.max(1, zMax - zMin);
        const t = clamp((worldZ - zMin) / span, 0, 1);
        stairHeight = fromHeight + (toHeight - fromHeight) * t;
        break;
    }

    let height;
    if (stairHeight != null) {
        height = stairHeight;
    } else if (worldZ >= boundaryLowerUpper) {
        height = lowerCityHeight;
    } else if (worldZ > boundaryCastleUpper) {
        height = upperCityHeight;
    } else {
        height = castleBaseHeight;
    }

    const noise = Math.sin(worldX * noiseScale + seed) * Math.cos(worldZ * noiseScale + seed * 0.7) * 0.5;
    height += noise * noiseAmplitude;
    return Math.max(0, Math.floor(height));
}

function getDungeonFloorTopY(terrain, floorIndex) {
    const dungeon = terrain?.dungeon || {};
    const floorCount = Math.max(1, Math.floor(Number(dungeon.floorCount) || 10));
    const floorHeight = Math.max(1, Math.floor(Number(dungeon.floorHeight) || 10));
    const idx = clamp(Math.floor(Number(floorIndex) || 1), 1, floorCount);
    return (floorCount - idx) * floorHeight + (floorHeight - 1);
}

function getDungeonFloorGroundY(terrain, floorIndex) {
    const groundOffset = Math.max(0, Math.floor(Number(terrain?.dungeon?.groundOffset) || 7));
    return getDungeonFloorTopY(terrain, floorIndex) - groundOffset;
}

function getDungeonFloorSurfaceY(terrain, floorIndex) {
    return getDungeonFloorGroundY(terrain, floorIndex) + 1;
}

function getNearestDungeonFloor(terrain, nearY) {
    const dungeon = terrain?.dungeon || {};
    const floorCount = Math.max(1, Math.floor(Number(dungeon.floorCount) || 10));
    let bestFloor = 1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let floor = 1; floor <= floorCount; floor++) {
        const surfaceY = getDungeonFloorSurfaceY(terrain, floor);
        const distance = Math.abs(surfaceY - nearY);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestFloor = floor;
        }
    }
    return bestFloor;
}

function normalizeBlockTypeId(value) {
    return String(value || '').trim().toUpperCase();
}

function getBlockFillAbsoluteRect(meta, terrain, blockFill) {
    const pos = Array.isArray(blockFill?.pos) ? blockFill.pos : [];
    const size = Array.isArray(blockFill?.size) ? blockFill.size : [];
    if (pos.length < 3 || size.length < 3) return null;

    const ref = resolveReferencePosition(meta, terrain, blockFill.posRelativeTo || 'world_center');
    const minX = Math.floor(Number(ref.x) + Number(pos[0]));
    const minY = Math.floor(Number(pos[1]));
    const minZ = Math.floor(Number(ref.z) + Number(pos[2]));
    const sizeX = Math.max(1, Math.floor(Number(size[0]) || 1));
    const sizeY = Math.max(1, Math.floor(Number(size[1]) || 1));
    const sizeZ = Math.max(1, Math.floor(Number(size[2]) || 1));

    return {
        id: String(blockFill?.id || ''),
        blockType: normalizeBlockTypeId(blockFill?.blockType),
        minX,
        minY,
        minZ,
        maxX: minX + sizeX - 1,
        maxY: minY + sizeY - 1,
        maxZ: minZ + sizeZ - 1,
        sizeX,
        sizeY,
        sizeZ
    };
}

function isWorldCeilingRect(rect, worldSize) {
    if (!rect || rect.sizeY !== 1) return false;
    return rect.minY >= MAX_BLOCK_Y && rect.sizeX >= worldSize.width - 2 && rect.sizeZ >= worldSize.depth - 2;
}

function isWallRect(rect) {
    return !!rect && (rect.sizeX === 1 || rect.sizeZ === 1);
}

function coversRectXZ(rect, x, z) {
    return !!rect && x >= rect.minX && x <= rect.maxX && z >= rect.minZ && z <= rect.maxZ;
}

function resolveVoidFloorRects(meta, terrain, objectsData) {
    const worldSize = getWorldSize(meta);
    const fills = Array.isArray(objectsData?.blockFills) ? objectsData.blockFills : [];
    const result = [];
    for (const blockFill of fills) {
        const rect = getBlockFillAbsoluteRect(meta, terrain, blockFill);
        if (!rect) continue;
        if (rect.blockType === 'AIR') continue;
        if (rect.sizeY !== 1) continue;
        if (isWallRect(rect)) continue;
        if (isWorldCeilingRect(rect, worldSize)) continue;
        if (rect.sizeX < 8 || rect.sizeZ < 8) continue;
        result.push({
            ...rect,
            surfaceY: clamp(rect.maxY + 1, 0, MAX_BLOCK_Y)
        });
    }
    return result;
}

/**
 * 高さヒント（nearY）を正規化する。null / undefined は「ヒント無し」。
 * Number(null) === 0 のため、素の Number.isFinite() で判定すると
 * 「ヒント無し」が「Y=0 のヒント」に化けて最下層へ吸い寄せられる。
 */
function toNearYHint(nearY) {
    if (nearY === null || nearY === undefined || nearY === '') return null;
    const value = Number(nearY);
    return Number.isFinite(value) ? value : null;
}

function resolveSurfaceY(meta, terrain, objectsData, x, z, nearY = null) {
    const worldSize = getWorldSize(meta);
    const ix = clamp(Math.floor(x), 0, worldSize.width - 1);
    const iz = clamp(Math.floor(z), 0, worldSize.depth - 1);
    const generator = String(terrain?.generator || 'overworld');
    const nearYHint = toNearYHint(nearY);

    let candidate;
    if (generator === 'dungeon') {
        const floor = nearYHint !== null ? getNearestDungeonFloor(terrain, nearYHint) : 1;
        return clamp(getDungeonFloorSurfaceY(terrain, floor), 0, MAX_BLOCK_Y);
    } else if (generator === 'void') {
        const floorRects = resolveVoidFloorRects(meta, terrain, objectsData);
        if (floorRects.length > 0) {
            const near = nearYHint;
            let bestRect = floorRects[0];
            let bestScore = Number.POSITIVE_INFINITY;
            for (const rect of floorRects) {
                const centerX = (rect.minX + rect.maxX) * 0.5;
                const centerZ = (rect.minZ + rect.maxZ) * 0.5;
                const distXZ = Math.hypot(centerX - ix, centerZ - iz);
                const distY = near == null ? 0 : Math.abs(rect.surfaceY - near) * 12;
                const score = distXZ + distY;
                if (score < bestScore) {
                    bestScore = score;
                    bestRect = rect;
                }
            }
            candidate = bestRect.surfaceY;
        } else {
            const groundLevel = Number(terrain?.elevation?.groundLevel);
            candidate = (Number.isFinite(groundLevel) ? Math.floor(groundLevel) : 0) + 1;
        }
    } else {
        candidate = getTerrainHeight(meta, terrain, ix, iz) + 1;
    }

    const fills = Array.isArray(objectsData?.blockFills) ? objectsData.blockFills : [];
    for (const blockFill of fills) {
        const rect = getBlockFillAbsoluteRect(meta, terrain, blockFill);
        if (!rect || rect.blockType === 'AIR') continue;
        if (!coversRectXZ(rect, ix, iz)) continue;
        if (isWallRect(rect)) continue;
        if (isWorldCeilingRect(rect, worldSize)) continue;

        const proposed = rect.maxY < MAX_BLOCK_Y ? rect.maxY + 1 : MAX_BLOCK_Y;
        if (proposed > candidate) {
            candidate = proposed;
        }
    }

    return clamp(Math.floor(candidate), 0, MAX_BLOCK_Y);
}

/**
 * objects.json のオブジェクト座標の基準点。
 * MapObjectSpawner.resolvePosition と同じく、posRelativeTo が無い場合は
 * ワールド中心ではなく絶対座標（0, 0 基準）として扱う。
 * （blockFill は resolveBlockFillFootprint 側の既定が world_center なので別扱い）
 */
function resolveObjectReferencePosition(meta, terrain, objectDef) {
    return objectDef?.posRelativeTo
        ? resolveReferencePosition(meta, terrain, objectDef.posRelativeTo)
        : { x: 0, z: 0 };
}

function resolveObjectBasePosition(meta, terrain, objectDef) {
    const base = resolveObjectReferencePosition(meta, terrain, objectDef);
    const pos = Array.isArray(objectDef?.pos) ? objectDef.pos : [];
    const x = Math.floor(Number(base.x) + Number(pos[0] || 0));
    const z = Math.floor(Number(base.z) + Number(pos[2] || 0));
    const explicitY = pos[1] !== undefined && Number(pos[1]) !== 0;

    if (explicitY) {
        return { x, y: Math.floor(Number(pos[1])), z };
    }

    const terrainHeight = getTerrainHeight(meta, terrain, x, z);
    if (objectDef?.type === 'building' || objectDef?.type === 'castle') {
        return { x, y: Math.max(0, terrainHeight - 1), z };
    }
    return { x, y: Math.max(0, terrainHeight), z };
}

function resolveEntityPosition(meta, terrain, entityDef) {
    const pos = Array.isArray(entityDef?.pos) ? entityDef.pos : null;
    if (pos && pos.length >= 3) {
        return {
            x: Math.floor(Number(pos[0]) || 0),
            y: Number.isFinite(Number(pos[1])) ? Number(pos[1]) : 0,
            z: Math.floor(Number(pos[2]) || 0)
        };
    }

    const posOffset = Array.isArray(entityDef?.posOffset) ? entityDef.posOffset : null;
    if (posOffset && posOffset.length >= 3) {
        const ref = resolveReferencePosition(meta, terrain, entityDef?.posRelativeTo || 'world_center');
        return {
            x: Math.floor(Number(ref.x) + Number(posOffset[0] || 0)),
            y: Number.isFinite(Number(posOffset[1])) ? Number(posOffset[1]) : 0,
            z: Math.floor(Number(ref.z) + Number(posOffset[2] || 0))
        };
    }

    return null;
}

function resolveAreaTypeWeight(area) {
    const type = String(area?.type || '').toLowerCase();
    if (/(throne|sanct|bulkhead|castle|chapel|temple|station|ruin|ruins)/.test(type)) return 150;
    if (/(corridor|deck|hall|mine|cavern|entrance|ridge|nest|gorge)/.test(type)) return 115;
    if (/(residential|building|bridge|crystal|maze|oasis|forest)/.test(type)) return 85;
    if (/(lake|water|toxic|magma)/.test(type)) return 25;
    return 55;
}

function isHazardArea(area) {
    const blockType = normalizeBlockTypeId(area?.surfaceBlock);
    if (HAZARD_SURFACE_BLOCKS.has(blockType)) return true;
    const type = String(area?.type || '').toLowerCase();
    return /(lake|water|toxic|magma)/.test(type);
}

function sampleAreaPosition(area, rng, worldSize) {
    if (!area || typeof area !== 'object') return null;
    const margin = 12;
    if (area.shape === 'circle' && area.center) {
        const radius = Math.max(8, Number(area.radius) || 8);
        const angle = rng() * Math.PI * 2;
        const dist = Math.sqrt(rng()) * Math.max(4, radius - margin);
        const x = clamp(Math.floor(Number(area.center.x) + Math.cos(angle) * dist), 1, worldSize.width - 2);
        const z = clamp(Math.floor(Number(area.center.z) + Math.sin(angle) * dist), 1, worldSize.depth - 2);
        return { x, z };
    }

    const bounds = area.bounds || {};
    const minX = clamp(Math.floor(Number(bounds.minX) || 0) + margin, 1, worldSize.width - 2);
    const maxX = clamp(Math.floor(Number(bounds.maxX) || worldSize.width - 1) - margin, 1, worldSize.width - 2);
    const minZ = clamp(Math.floor(Number(bounds.minZ) || 0) + margin, 1, worldSize.depth - 2);
    const maxZ = clamp(Math.floor(Number(bounds.maxZ) || worldSize.depth - 1) - margin, 1, worldSize.depth - 2);

    if (minX > maxX || minZ > maxZ) {
        return {
            x: clamp(Math.floor(Number(bounds.minX) || 0), 1, worldSize.width - 2),
            z: clamp(Math.floor(Number(bounds.minZ) || 0), 1, worldSize.depth - 2)
        };
    }

    return {
        x: pickRandomInt(rng, minX, maxX),
        z: pickRandomInt(rng, minZ, maxZ)
    };
}

function distanceToNearestPoint(x, z, points) {
    if (!Array.isArray(points) || points.length <= 0) return Number.POSITIVE_INFINITY;
    let best = Number.POSITIVE_INFINITY;
    for (const point of points) {
        const dx = x - point.x;
        const dz = z - point.z;
        const dist = Math.hypot(dx, dz);
        if (dist < best) best = dist;
    }
    return best;
}

function candidateKey(x, y, z) {
    return `${Math.floor(x)},${Math.floor(y)},${Math.floor(z)}`;
}

function normalizeCandidate(meta, x, y, z, score, source, extra = {}) {
    const worldSize = getWorldSize(meta);
    const edgeMargin = Math.min(8, Math.floor((Math.min(worldSize.width, worldSize.depth) - 1) / 4));
    return {
        x: clamp(Math.floor(x), edgeMargin, worldSize.width - 1 - edgeMargin),
        y: clamp(Math.floor(y), 0, MAX_BLOCK_Y),
        z: clamp(Math.floor(z), edgeMargin, worldSize.depth - 1 - edgeMargin),
        score,
        source,
        ...extra
    };
}

function resolveDefaultBuildingType(mapData) {
    return mapData?.objectsData?.defaultBuildingType
        || mapData?.meta?.ui?.defaultBuildingType
        || mapData?.meta?.defaultBuildingType
        || null;
}

function resolveBuildingTypeConfig(mapData, objectDef) {
    const buildingType = String(
        objectDef?.buildingType
        || resolveDefaultBuildingType(mapData)
        || ''
    ).trim();
    if (!buildingType) return null;

    const buildingTypeSettings = (mapData?.objectsData?.buildingTypeSettings && typeof mapData.objectsData.buildingTypeSettings === 'object')
        ? mapData.objectsData.buildingTypeSettings
        : {};
    const config = buildingTypeSettings[buildingType];
    return (config && typeof config === 'object') ? config : null;
}

/**
 * BuildingGenerator._seededRandom と同じ疑似乱数（seed=12345 固定）。
 * 寸法未指定の建物をエンジンと同じ大きさで再現するために使う。
 */
function buildingSeededRandom(x, z, offset = 0) {
    const hash = (Math.imul(x, 2654435761) ^ Math.imul(z, 2246822519) ^ Math.imul(offset, 3266489917) ^ 12345) >>> 0;
    return (hash % 10000) / 10000;
}

/**
 * エンジン（BuildingGenerator.generateBuilding）が実際に建てる寸法を再現する。
 * 明示寸法があればそれを、無ければ buildingTypeSettings の min/max から決定論的に生成する。
 */
function resolveConfiguredDimension(primaryValue, minValue, maxValue, hardFallback, seedX, seedZ, seedOffset) {
    const explicit = Number(primaryValue);
    if (Number.isFinite(explicit) && explicit > 0) {
        return Math.max(1, Math.floor(explicit));
    }

    const min = Number(minValue);
    const max = Number(maxValue);
    if (Number.isFinite(min) && Number.isFinite(max) && max >= min) {
        const generated = Math.floor(min + buildingSeededRandom(seedX, seedZ, seedOffset) * (max - min));
        return Math.max(1, generated);
    }
    if (Number.isFinite(min) && min > 0) {
        return Math.max(1, Math.floor(min));
    }
    if (Number.isFinite(max) && max > 0) {
        return Math.max(1, Math.floor(max));
    }
    return Math.max(1, Math.floor(hardFallback));
}

function resolveBuildingFootprint(mapData, objectDef) {
    const base = resolveObjectBasePosition(mapData.meta, mapData.terrain, objectDef);
    const typeConfig = resolveBuildingTypeConfig(mapData, objectDef);
    const width = resolveConfiguredDimension(
        objectDef?.width,
        typeConfig?.minWidth,
        typeConfig?.maxWidth,
        10,
        base.x,
        base.z,
        0
    );
    const depth = resolveConfiguredDimension(
        objectDef?.depth,
        typeConfig?.minDepth,
        typeConfig?.maxDepth,
        10,
        base.x,
        base.z,
        1
    );

    return {
        id: String(objectDef?.id || `building:${base.x}:${base.z}`),
        x: base.x,
        // 建物の 1 階床ブロックの Y（MapObjectSpawner.resolvePosition と同じ計算）
        baseY: base.y,
        z: base.z,
        width,
        depth,
        centerX: base.x + Math.floor((width - 1) * 0.5),
        centerZ: base.z + Math.floor((depth - 1) * 0.5)
    };
}

function resolveCastlePlacementSpec(objectDef, mapData) {
    const center = resolveObjectBasePosition(mapData.meta, mapData.terrain, objectDef);
    const platformRadius = Math.max(12, Math.floor(Number(objectDef?.config?.platform?.size) || 50));
    const wallRadius = Math.max(12, Math.floor(Number(objectDef?.config?.walls?.radius) || platformRadius));
    const mainKeepHalfWidth = Math.max(8, Math.floor(Number(objectDef?.config?.mainKeep?.width) || 18));
    return {
        id: String(objectDef?.id || `castle:${center.x}:${center.z}`),
        centerX: center.x,
        centerZ: center.z,
        platformRadius,
        wallRadius,
        keepRadius: mainKeepHalfWidth + 3
    };
}

function getPlacementExclusions(mapData) {
    if (Array.isArray(mapData._treasurePlacementExclusions)) {
        return mapData._treasurePlacementExclusions;
    }

    const exclusions = [];
    const objects = Array.isArray(mapData?.objectsData?.objects) ? mapData.objectsData.objects : [];
    for (const objectDef of objects) {
        if (objectDef?.type === 'building') {
            const footprint = resolveBuildingFootprint(mapData, objectDef);
            exclusions.push({
                id: `building:${footprint.id}`,
                shape: 'rect',
                minX: footprint.x,
                maxX: footprint.x + footprint.width - 1,
                minZ: footprint.z,
                maxZ: footprint.z + footprint.depth - 1,
                margin: 1
            });
            continue;
        }

        if (objectDef?.type === 'castle') {
            const castleSpec = resolveCastlePlacementSpec(objectDef, mapData);
            exclusions.push({
                id: `castle:${castleSpec.id}:keep`,
                shape: 'circle',
                centerX: castleSpec.centerX,
                centerZ: castleSpec.centerZ,
                radius: castleSpec.keepRadius,
                margin: 2
            });
        }
    }

    mapData._treasurePlacementExclusions = exclusions;
    return exclusions;
}

function isCandidateInsidePlacementExclusion(mapData, x, z, options = {}) {
    const exclusions = getPlacementExclusions(mapData);
    const ignoreIds = options.ignoreIds instanceof Set ? options.ignoreIds : new Set();

    for (const exclusion of exclusions) {
        if (ignoreIds.has(exclusion.id)) continue;

        const margin = Math.max(0, Math.floor(Number(exclusion.margin) || 0));
        if (exclusion.shape === 'rect') {
            if (
                x >= exclusion.minX - margin &&
                x <= exclusion.maxX + margin &&
                z >= exclusion.minZ - margin &&
                z <= exclusion.maxZ + margin
            ) {
                return true;
            }
            continue;
        }

        if (exclusion.shape === 'circle') {
            const dx = x - exclusion.centerX;
            const dz = z - exclusion.centerZ;
            if (Math.hypot(dx, dz) <= exclusion.radius + margin) {
                return true;
            }
        }
    }

    return false;
}

function createBuildingAnchorPoints(footprint, margin) {
    const minX = footprint.x;
    const maxX = footprint.x + footprint.width - 1;
    const minZ = footprint.z;
    const maxZ = footprint.z + footprint.depth - 1;
    const centerX = footprint.centerX;
    const centerZ = footprint.centerZ;
    return [
        { x: centerX, z: minZ - margin, side: 'north' },
        { x: centerX, z: maxZ + margin, side: 'south' },
        { x: minX - margin, z: centerZ, side: 'west' },
        { x: maxX + margin, z: centerZ, side: 'east' },
        { x: minX - margin, z: minZ - margin, side: 'northwest' },
        { x: maxX + margin, z: minZ - margin, side: 'northeast' },
        { x: minX - margin, z: maxZ + margin, side: 'southwest' },
        { x: maxX + margin, z: maxZ + margin, side: 'southeast' }
    ];
}

function createCastleAnchorPoints(castleSpec, margin) {
    const radius = castleSpec.wallRadius + margin;
    return [
        { x: castleSpec.centerX, z: castleSpec.centerZ - radius, side: 'north' },
        { x: castleSpec.centerX, z: castleSpec.centerZ + radius, side: 'south' },
        { x: castleSpec.centerX - radius, z: castleSpec.centerZ, side: 'west' },
        { x: castleSpec.centerX + radius, z: castleSpec.centerZ, side: 'east' },
        { x: castleSpec.centerX - radius, z: castleSpec.centerZ - radius, side: 'northwest' },
        { x: castleSpec.centerX + radius, z: castleSpec.centerZ - radius, side: 'northeast' },
        { x: castleSpec.centerX - radius, z: castleSpec.centerZ + radius, side: 'southwest' },
        { x: castleSpec.centerX + radius, z: castleSpec.centerZ + radius, side: 'southeast' }
    ];
}

function clusterPoints(points, radius) {
    const result = [];
    const visited = new Uint8Array(points.length);

    for (let index = 0; index < points.length; index++) {
        if (visited[index]) continue;

        const queue = [index];
        const members = [];
        visited[index] = 1;

        while (queue.length > 0) {
            const currentIndex = queue.shift();
            const current = points[currentIndex];
            members.push(current);

            for (let otherIndex = 0; otherIndex < points.length; otherIndex++) {
                if (visited[otherIndex]) continue;
                const other = points[otherIndex];
                if (Math.hypot(current.x - other.x, current.z - other.z) > radius) continue;
                visited[otherIndex] = 1;
                queue.push(otherIndex);
            }
        }

        const sum = members.reduce((acc, member) => {
            acc.x += member.x;
            acc.y += Number(member.y) || 0;
            acc.z += member.z;
            return acc;
        }, { x: 0, y: 0, z: 0 });

        result.push({
            id: result.length + 1,
            centerX: Math.round(sum.x / members.length),
            centerY: Math.round(sum.y / members.length),
            centerZ: Math.round(sum.z / members.length),
            size: members.length,
            points: members
        });
    }

    return result;
}

function createSelectionOptions(meta, requiredCount, minDistance, options = {}) {
    const worldSize = getWorldSize(meta);
    const defaultCellSize = Math.max(
        minDistance * 4,
        Math.floor(Math.sqrt((worldSize.width * worldSize.depth) / Math.max(1, requiredCount)) * 1.1)
    );
    return {
        minDistance,
        cellSize: Math.max(minDistance * 3, Math.floor(Number(options.cellSize) || defaultCellSize)),
        maxPerCell: Math.max(1, Math.floor(Number(options.maxPerCell) || (requiredCount >= 20 ? 2 : 1)))
    };
}

function buildBuildingCandidates(mapData, rng) {
    const { meta, terrain, objectsData, enemies } = mapData;
    const worldSize = getWorldSize(meta);
    const enemyPoints = enemies
        .map((enemy) => resolveEntityPosition(meta, terrain, enemy))
        .filter(Boolean)
        .map((point) => ({ x: point.x, z: point.z }));

    const results = [];
    const objects = Array.isArray(objectsData?.objects) ? objectsData.objects : [];
    for (const objectDef of objects) {
        if (objectDef?.type !== 'building' && objectDef?.type !== 'castle') continue;
        const text = JSON.stringify({
            id: objectDef?.id,
            type: objectDef?.type,
            variant: objectDef?.variant,
            subtype: objectDef?.subtype,
            areaId: objectDef?.areaId
        });
        const isRuin = RUIN_KEYWORDS.test(text);
        const isMajor = objectDef?.type === 'castle' || MAJOR_STRUCTURE_KEYWORDS.test(text);
        const sourceGroupId = `${objectDef.type}:${String(objectDef?.id || '').trim() || 'unnamed'}`;

        if (objectDef?.type === 'castle') {
            const castleSpec = resolveCastlePlacementSpec(objectDef, mapData);
            const enemyDistance = distanceToNearestPoint(castleSpec.centerX, castleSpec.centerZ, enemyPoints);
            const proximityBonus = Number.isFinite(enemyDistance) ? Math.max(0, 80 - enemyDistance * 1.5) : 0;
            const baseScore = 300 + (isRuin ? 35 : 0) + 65 + proximityBonus;
            const margin = Math.max(6, Math.floor(castleSpec.wallRadius * 0.08));
            const anchors = createCastleAnchorPoints(castleSpec, margin);
            const ignoreIds = new Set([`castle:${castleSpec.id}:keep`]);

            for (let index = 0; index < anchors.length; index++) {
                const anchor = anchors[index];
                const x = clamp(anchor.x, 1, worldSize.width - 2);
                const z = clamp(anchor.z, 1, worldSize.depth - 2);
                if (isCandidateInsidePlacementExclusion(mapData, x, z, { ignoreIds })) continue;
                const y = resolveSurfaceY(meta, terrain, objectsData, x, z, castleSpec.centerY);
                const score = baseScore - index * 6 + rng() * 3;
                results.push(normalizeCandidate(meta, x, y, z, score, 'building', {
                    isRuin,
                    isMajor,
                    sourceGroupId,
                    sourceGroupLimit: 2
                }));
            }
            continue;
        }

        const footprint = resolveBuildingFootprint(mapData, objectDef);
        const centerSurfaceY = resolveSurfaceY(meta, terrain, objectsData, footprint.centerX, footprint.centerZ, footprint.baseY);
        const enemyDistance = distanceToNearestPoint(footprint.centerX, footprint.centerZ, enemyPoints);
        const proximityBonus = Number.isFinite(enemyDistance) ? Math.max(0, 70 - enemyDistance * 1.2) : 0;
        const baseScore = 240 + (isRuin ? 45 : 0) + (isMajor ? 35 : 0) + proximityBonus;
        const margin = Math.max(2, Math.min(8, Math.floor(Math.max(footprint.width, footprint.depth) / 5)));
        const anchors = createBuildingAnchorPoints(footprint, margin);
        const ignoreIds = new Set([`building:${footprint.id}`]);

        for (let index = 0; index < anchors.length; index++) {
            const anchor = anchors[index];
            const x = clamp(anchor.x, 1, worldSize.width - 2);
            const z = clamp(anchor.z, 1, worldSize.depth - 2);
            if (isCandidateInsidePlacementExclusion(mapData, x, z, { ignoreIds })) continue;
            const y = resolveSurfaceY(meta, terrain, objectsData, x, z, centerSurfaceY);
            const score = baseScore - index * 5 + rng() * 3;
            results.push(normalizeCandidate(meta, x, y, z, score, 'building', {
                isRuin,
                isMajor,
                sourceGroupId,
                sourceGroupLimit: 1
            }));
        }
    }
    return results;
}

/* =============================================================================
 * 建造物内の宝箱（2026-08-02 指示）
 *
 * 家・塔・城などの建造物には必ず 1 個以上の宝箱を「内部」へ置き、大きい建造物ほど
 * 大きさに比例して増やす。上階ほど価値が高くなるよう floorIndex を持たせ、
 * ランク割当（assignTreasureTypes）で下階より必ず上位になるよう補正する。
 *
 * 位置はエンジン（building-generator.ts / castle-generator.ts / MapObjectSpawner の
 * blockFill シェル）の幾何をそのまま再現して求め、壁・ドア正面・階段レーンを避ける。
 * 階段レーンは「上階の床が頭上クリアランスで抜かれている」ため、避けないと宝箱が
 * 下階へ落ちる。
 * ============================================================================= */

function toPositiveIntOr(value, fallback) {
    const number = Math.floor(Number(value));
    return Number.isFinite(number) && number > 0 ? number : fallback;
}

function toNonNegativeIntOr(value, fallback) {
    const number = Math.floor(Number(value));
    return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function resolveInteriorPolicy(policy) {
    const raw = (policy && typeof policy.buildingInterior === 'object' && policy.buildingInterior) || {};
    return {
        enabled: raw.enabled !== false,
        volumePerChest: toPositiveIntOr(raw.volumePerChest, BUILDING_INTERIOR_DEFAULTS.volumePerChest),
        maxChestsPerStructure: toPositiveIntOr(raw.maxChestsPerStructure, BUILDING_INTERIOR_DEFAULTS.maxChestsPerStructure),
        maxChestsPerFloor: toPositiveIntOr(raw.maxChestsPerFloor, BUILDING_INTERIOR_DEFAULTS.maxChestsPerFloor),
        minChestSeparation: toPositiveIntOr(raw.minChestSeparation, BUILDING_INTERIOR_DEFAULTS.minChestSeparation)
    };
}

/** 内寸体積（内寸幅 × 内寸奥行 × 階数）に比例した宝箱の個数。最低 1 個は必ず置く。 */
function resolveInteriorChestCount(interiorWidth, interiorDepth, floors, interiorPolicy) {
    const safeFloors = Math.max(1, Math.floor(floors));
    const volume = Math.max(0, interiorWidth) * Math.max(0, interiorDepth) * safeFloors;
    const byVolume = Math.round(volume / interiorPolicy.volumePerChest);
    const upperLimit = Math.min(
        interiorPolicy.maxChestsPerStructure,
        interiorPolicy.maxChestsPerFloor * safeFloors
    );
    return clamp(byVolume, 1, Math.max(1, upperLimit));
}

/** 宝箱を置く階の優先順（最上階 → 1 階 → 上から順）。1 個しか置けない建物は最上階になる。 */
function resolveInteriorFloorOrder(floors) {
    const order = [];
    const pushed = new Set();
    const push = (floorIndex) => {
        if (floorIndex < 0 || floorIndex >= floors || pushed.has(floorIndex)) return;
        pushed.add(floorIndex);
        order.push(floorIndex);
    };
    push(floors - 1);
    push(0);
    for (let floorIndex = floors - 2; floorIndex >= 1; floorIndex--) push(floorIndex);
    return order;
}

/** 個数を階へ配る（優先順に 1 個ずつ、1 フロアの上限まで）。 */
function distributeChestsToFloors(count, floors, maxChestsPerFloor) {
    const order = resolveInteriorFloorOrder(floors);
    const perFloor = new Map();
    let remaining = Math.max(0, Math.floor(count));

    while (remaining > 0) {
        let placedInPass = 0;
        for (const floorIndex of order) {
            if (remaining <= 0) break;
            const current = perFloor.get(floorIndex) || 0;
            if (current >= maxChestsPerFloor) continue;
            perFloor.set(floorIndex, current + 1);
            remaining -= 1;
            placedInPass += 1;
        }
        if (placedInPass === 0) break;
    }

    return perFloor;
}

/** 壁際スパイラル階段が占める内部セル（局所座標）。 */
function collectWallSpiralStairCells(stairConfig, width, depth, cells) {
    const usableX = Math.max(1, width - 2);
    const usableZ = Math.max(1, depth - 2);
    const maxSpiralWidth = Math.max(1, Math.min(usableX, usableZ));
    if (maxSpiralWidth < 2) return;

    const stairWidth = Math.max(2, Math.min(toPositiveIntOr(stairConfig?.width, 2), maxSpiralWidth));
    const maxInset = Math.max(0, Math.floor((Math.min(usableX, usableZ) - 1) / 2));
    const wallInset = Math.min(toNonNegativeIntOr(stairConfig?.wallInset ?? stairConfig?.inset, 0), maxInset);
    const minX = 1 + wallInset;
    const maxX = width - 2 - wallInset;
    const minZ = 1 + wallInset;
    const maxZ = depth - 2 - wallInset;
    if (minX > maxX || minZ > maxZ) return;

    for (let localX = minX; localX <= maxX; localX++) {
        for (let localZ = minZ; localZ <= maxZ; localZ++) {
            const inward = Math.min(localX - minX, maxX - localX, localZ - minZ, maxZ - localZ);
            if (inward < stairWidth) cells.add(`${localX},${localZ}`);
        }
    }
}

/** 直線階段が占める内部セル（局所座標）。走行レーン全体を保守的に除外する。 */
function collectLinearStairCells(stairConfig, width, depth, cells) {
    const axis = stairConfig?.axis === 'z' ? 'z' : 'x';
    const sign = Number(stairConfig?.sign ?? stairConfig?.direction) < 0 ? -1 : 1;
    const usableX = Math.max(1, width - 2);
    const usableZ = Math.max(1, depth - 2);
    const stairWidth = Math.max(1, Math.min(
        toPositiveIntOr(stairConfig?.width, 1),
        axis === 'x' ? usableZ : usableX
    ));
    const startOffset = Array.isArray(stairConfig?.startOffset)
        ? stairConfig.startOffset
        : (Array.isArray(stairConfig?.start) ? stairConfig.start : [1, 1]);
    const maxStartOffsetX = axis === 'z' ? Math.max(1, width - stairWidth - 1) : Math.max(1, width - 2);
    const maxStartOffsetZ = axis === 'x' ? Math.max(1, depth - stairWidth - 1) : Math.max(1, depth - 2);
    const startOffsetX = clamp(toNonNegativeIntOr(startOffset[0], 1), 1, maxStartOffsetX);
    const startOffsetZ = clamp(toNonNegativeIntOr(startOffset[1], 1), 1, maxStartOffsetZ);

    if (axis === 'x') {
        const fromX = sign > 0 ? startOffsetX : 1;
        const toX = sign > 0 ? width - 2 : startOffsetX;
        for (let localX = fromX; localX <= toX; localX++) {
            for (let lane = 0; lane < stairWidth; lane++) {
                cells.add(`${localX},${startOffsetZ + lane}`);
            }
        }
        return;
    }

    const fromZ = sign > 0 ? startOffsetZ : 1;
    const toZ = sign > 0 ? depth - 2 : startOffsetZ;
    for (let localZ = fromZ; localZ <= toZ; localZ++) {
        for (let lane = 0; lane < stairWidth; lane++) {
            cells.add(`${startOffsetX + lane},${localZ}`);
        }
    }
}

function isWallSpiralStairs(stairConfig) {
    const pattern = String(stairConfig?.pattern ?? stairConfig?.layout ?? stairConfig?.mode ?? 'linear').toLowerCase();
    return pattern === 'wallspiral' || pattern === 'wall_spiral' || pattern === 'spiral';
}

/** 屋内階段が占める内部セル（局所座標）。2 階建て以上で階段が有効なときだけ発生する。 */
function collectBuildingStairCells(stairConfig, width, depth, floors) {
    const cells = new Set();
    if (floors < 2) return cells;
    if (!stairConfig || typeof stairConfig !== 'object' || stairConfig.enabled === false) return cells;

    if (isWallSpiralStairs(stairConfig)) {
        collectWallSpiralStairCells(stairConfig, width, depth, cells);
    } else {
        collectLinearStairCells(stairConfig, width, depth, cells);
    }
    return cells;
}

/** BuildingGenerator.buildWallSpiralPath と同じ経路（局所座標）。 */
function buildWallSpiralPath(width, depth, wallInset) {
    const minX = 1 + wallInset;
    const maxX = width - 2 - wallInset;
    const minZ = 1 + wallInset;
    const maxZ = depth - 2 - wallInset;
    if (minX > maxX || minZ > maxZ) return [];

    const path = [];
    for (let localX = minX; localX <= maxX; localX++) path.push({ x: localX, z: minZ, inwardX: 0, inwardZ: 1 });
    for (let localZ = minZ + 1; localZ <= maxZ; localZ++) path.push({ x: maxX, z: localZ, inwardX: -1, inwardZ: 0 });
    if (maxZ > minZ) {
        for (let localX = maxX - 1; localX >= minX; localX--) path.push({ x: localX, z: maxZ, inwardX: 0, inwardZ: -1 });
    }
    if (maxX > minX) {
        for (let localZ = maxZ - 1; localZ > minZ; localZ--) path.push({ x: minX, z: localZ, inwardX: 1, inwardZ: 0 });
    }
    return path;
}

/** BuildingGenerator.resolveSpiralStartIndex と同じ開始インデックス。 */
function resolveSpiralStartIndex(path, startAnchorRaw, startStepOffsetRaw = 0) {
    const pathLength = path.length;
    if (pathLength === 0) return 0;

    let startIndex = 0;
    if (Number.isFinite(Number(startAnchorRaw)) && typeof startAnchorRaw !== 'string' && !Array.isArray(startAnchorRaw)) {
        startIndex = Math.floor(Number(startAnchorRaw));
    } else {
        const minX = Math.min(...path.map((point) => point.x));
        const maxX = Math.max(...path.map((point) => point.x));
        const minZ = Math.min(...path.map((point) => point.z));
        const maxZ = Math.max(...path.map((point) => point.z));
        const midX = (minX + maxX) / 2;
        const midZ = (minZ + maxZ) / 2;

        let targetX = midX;
        let targetZ = minZ;
        if (Array.isArray(startAnchorRaw) && startAnchorRaw.length >= 2) {
            targetX = Number.isFinite(Number(startAnchorRaw[0])) ? Number(startAnchorRaw[0]) : midX;
            targetZ = Number.isFinite(Number(startAnchorRaw[1])) ? Number(startAnchorRaw[1]) : minZ;
        } else {
            const anchor = String(startAnchorRaw || 'northCenter').toLowerCase();
            if (anchor === 'southcenter') targetZ = maxZ;
            else if (anchor === 'eastcenter') { targetX = maxX; targetZ = midZ; }
            else if (anchor === 'westcenter') { targetX = minX; targetZ = midZ; }
            else if (anchor === 'northwest') { targetX = minX; targetZ = minZ; }
            else if (anchor === 'northeast') { targetX = maxX; targetZ = minZ; }
            else if (anchor === 'southwest') { targetX = minX; targetZ = maxZ; }
            else if (anchor === 'southeast') { targetX = maxX; targetZ = maxZ; }
        }

        let bestScore = Number.POSITIVE_INFINITY;
        for (let index = 0; index < pathLength; index++) {
            const score = Math.abs(path[index].x - targetX) + Math.abs(path[index].z - targetZ);
            if (score < bestScore) {
                bestScore = score;
                startIndex = index;
            }
        }
    }

    const stepOffset = Math.floor(Number(startStepOffsetRaw));
    const normalizedOffset = Number.isFinite(stepOffset) ? stepOffset : 0;
    return ((startIndex + normalizedOffset) % pathLength + pathLength) % pathLength;
}

/**
 * 1 階の最初の段（床の 1 つ上 = 宝箱を置く高さ）が乗るセル（局所座標）。
 * 内部が階段レーンで埋まる小さな建物でも、ここさえ避ければ床の上に置ける。
 */
function collectGroundFlightFirstStepCells(stairConfig, width, depth, floors) {
    const cells = new Set();
    if (floors < 2) return cells;
    if (!stairConfig || typeof stairConfig !== 'object' || stairConfig.enabled === false) return cells;

    const usableX = Math.max(1, width - 2);
    const usableZ = Math.max(1, depth - 2);
    const runPerRise = toPositiveIntOr(stairConfig.runPerRise, 1);

    if (isWallSpiralStairs(stairConfig)) {
        const maxSpiralWidth = Math.max(1, Math.min(usableX, usableZ));
        if (maxSpiralWidth < 2) return cells;
        const stairWidth = Math.max(2, Math.min(toPositiveIntOr(stairConfig.width, 2), maxSpiralWidth));
        const maxInset = Math.max(0, Math.floor((Math.min(usableX, usableZ) - 1) / 2));
        const wallInset = Math.min(toNonNegativeIntOr(stairConfig.wallInset ?? stairConfig.inset, 0), maxInset);
        let path = buildWallSpiralPath(width, depth, wallInset);
        if (path.length === 0) return cells;
        if (stairConfig.clockwise === false) path = [...path].reverse();
        const startIndex = resolveSpiralStartIndex(
            path,
            stairConfig.startAnchor ?? stairConfig.start ?? 'northCenter',
            stairConfig.startStepOffset ?? stairConfig.startOffsetSteps ?? 0
        );

        for (let run = 0; run < runPerRise; run++) {
            const point = path[(startIndex + run) % path.length];
            for (let lane = 0; lane < stairWidth; lane++) {
                const localX = point.x + point.inwardX * lane;
                const localZ = point.z + point.inwardZ * lane;
                if (localX <= 0 || localX >= width - 1) continue;
                if (localZ <= 0 || localZ >= depth - 1) continue;
                cells.add(`${localX},${localZ}`);
            }
        }
        return cells;
    }

    const axis = stairConfig.axis === 'z' ? 'z' : 'x';
    const stairWidth = Math.max(1, Math.min(
        toPositiveIntOr(stairConfig.width, 1),
        axis === 'x' ? usableZ : usableX
    ));
    const startOffset = Array.isArray(stairConfig.startOffset)
        ? stairConfig.startOffset
        : (Array.isArray(stairConfig.start) ? stairConfig.start : [1, 1]);
    const maxStartOffsetX = axis === 'z' ? Math.max(1, width - stairWidth - 1) : Math.max(1, width - 2);
    const maxStartOffsetZ = axis === 'x' ? Math.max(1, depth - stairWidth - 1) : Math.max(1, depth - 2);
    const startOffsetX = clamp(toNonNegativeIntOr(startOffset[0], 1), 1, maxStartOffsetX);
    const startOffsetZ = clamp(toNonNegativeIntOr(startOffset[1], 1), 1, maxStartOffsetZ);
    const sign = Number(stairConfig.sign ?? stairConfig.direction) < 0 ? -1 : 1;

    for (let run = 0; run < runPerRise; run++) {
        for (let lane = 0; lane < stairWidth; lane++) {
            const localX = axis === 'x' ? startOffsetX + sign * run : startOffsetX + lane;
            const localZ = axis === 'x' ? startOffsetZ + lane : startOffsetZ + sign * run;
            cells.add(`${localX},${localZ}`);
        }
    }
    return cells;
}

/**
 * 建造物内に立っている NPC の足元（局所座標・周囲 1 マス）。
 * 店主や姫など屋内 NPC の位置へ宝箱ブロックを置かないために除外する。
 */
function collectNpcLocalCells(npcPoints, originX, originZ, width, depth) {
    const cells = new Set();
    for (const point of npcPoints) {
        for (let dx = -1; dx <= 1; dx++) {
            for (let dz = -1; dz <= 1; dz++) {
                const localX = point.x + dx - originX;
                const localZ = point.z + dz - originZ;
                if (localX < 1 || localX > width - 2) continue;
                if (localZ < 1 || localZ > depth - 2) continue;
                cells.add(`${localX},${localZ}`);
            }
        }
    }
    return cells;
}

/** ドアの内側 2 マス（局所座標）。1 階の出入口を宝箱で塞がないために除外する。 */
function collectBuildingDoorCells(objectDef, typeConfig, width, depth) {
    const cells = new Set();
    const doorCount = toPositiveIntOr(objectDef?.doorCount ?? typeConfig?.doorCount, 1);
    const doorWidth = toPositiveIntOr(objectDef?.doorWidth ?? typeConfig?.doorWidth, 1);
    const doorSide = String(objectDef?.doorSide ?? typeConfig?.doorSide ?? 'south');
    const isNorthSouth = doorSide === 'north' || doorSide === 'south';
    const axisLength = isNorthSouth ? width : depth;
    const usable = Math.max(1, axisLength - 2);
    const count = Math.min(doorCount, usable);
    const widthClamped = Math.min(doorWidth, usable);

    for (let index = 0; index < count; index++) {
        const center = 1 + Math.floor((index + 0.5) * usable / count);
        const start = Math.max(1, Math.min(axisLength - widthClamped - 1, center - Math.floor(widthClamped / 2)));
        for (let offsetIndex = 0; offsetIndex < widthClamped; offsetIndex++) {
            const offset = start + offsetIndex;
            for (let inward = 1; inward <= 2; inward++) {
                if (doorSide === 'south') cells.add(`${offset},${inward}`);
                else if (doorSide === 'north') cells.add(`${offset},${depth - 1 - inward}`);
                else if (doorSide === 'west') cells.add(`${inward},${offset}`);
                else cells.add(`${width - 1 - inward},${offset}`);
            }
        }
    }
    return cells;
}

/**
 * 内寸を等分した区画ごとに「壁からいちばん遠い空きセル」を選ぶ。
 * width / depth は壁を含む外形サイズで、内部セルは 1..width-2 / 1..depth-2。
 */
function pickInteriorCells(width, depth, blockedCells, count, minSeparation, seedPrefix) {
    const requested = Math.max(0, Math.floor(count));
    if (requested <= 0 || width < 3 || depth < 3) return [];

    const freeCells = [];
    for (let localX = 1; localX <= width - 2; localX++) {
        for (let localZ = 1; localZ <= depth - 2; localZ++) {
            if (blockedCells.has(`${localX},${localZ}`)) continue;
            freeCells.push({
                localX,
                localZ,
                wallDistance: Math.min(localX, width - 1 - localX, localZ, depth - 1 - localZ),
                order: hashString(`${seedPrefix}:${localX}:${localZ}`)
            });
        }
    }
    if (freeCells.length === 0) return [];

    // 長辺方向へ等分した区画ごとに 1 個ずつ選び、内部で固まらないよう散らす
    const parts = Math.min(requested, freeCells.length);
    const alongX = width >= depth;
    const bandMin = 1;
    const bandMax = (alongX ? width : depth) - 2;
    const bandSpan = Math.max(1, bandMax - bandMin + 1);
    const bands = Array.from({ length: parts }, () => []);
    for (const cell of freeCells) {
        const position = alongX ? cell.localX : cell.localZ;
        const bandIndex = clamp(Math.floor((position - bandMin) * parts / bandSpan), 0, parts - 1);
        bands[bandIndex].push(cell);
    }
    const byPreference = (a, b) => (b.wallDistance - a.wallDistance) || (a.order - b.order);
    for (const band of bands) band.sort(byPreference);
    const allSorted = [...freeCells].sort(byPreference);

    const picked = [];
    const usedKeys = new Set();
    const isFarEnough = (cell) => picked.every((selected) => Math.max(
        Math.abs(selected.localX - cell.localX),
        Math.abs(selected.localZ - cell.localZ)
    ) >= minSeparation);

    for (let bandIndex = 0; bandIndex < parts; bandIndex++) {
        const pools = [bands[bandIndex], allSorted];
        let chosen = null;
        // 区画内 → 全体、の順に「離れている空きセル」を探し、無ければ距離条件を外す
        for (const requireSeparation of [true, false]) {
            for (const pool of pools) {
                chosen = pool.find((cell) => !usedKeys.has(`${cell.localX},${cell.localZ}`)
                    && (!requireSeparation || isFarEnough(cell))) || null;
                if (chosen) break;
            }
            if (chosen) break;
        }
        if (!chosen) break;
        usedKeys.add(`${chosen.localX},${chosen.localZ}`);
        picked.push(chosen);
    }

    return picked;
}

/** マップ内の NPC 立ち位置（宝箱ブロックで潰さないための回避点）。 */
function resolveNpcPoints(mapData) {
    const npcs = Array.isArray(mapData.npcsData?.npcs) ? mapData.npcsData.npcs : [];
    return npcs
        .map((npcDef) => resolveEntityPosition(mapData.meta, mapData.terrain, npcDef))
        .filter(Boolean)
        .map((point) => ({ x: point.x, z: point.z }));
}

/** objects.json の building 1 件分の内部宝箱。 */
function buildBuildingInteriorPlacements(mapData, interiorPolicy, npcPoints) {
    const { meta, objectsData } = mapData;
    const worldSize = getWorldSize(meta);
    const placements = [];
    const objects = Array.isArray(objectsData?.objects) ? objectsData.objects : [];

    for (const objectDef of objects) {
        if (objectDef?.type !== 'building') continue;

        const footprint = resolveBuildingFootprint(mapData, objectDef);
        const interiorWidth = footprint.width - 2;
        const interiorDepth = footprint.depth - 2;
        if (interiorWidth < 1 || interiorDepth < 1) continue; // 内部空間の無い装飾建物

        const typeConfig = resolveBuildingTypeConfig(mapData, objectDef) || {};
        const floors = toPositiveIntOr(objectDef?.floors ?? typeConfig.floors, 1);
        const configuredFloorHeight = toPositiveIntOr(objectDef?.floorHeight ?? typeConfig.floorHeight, 5);
        // BuildingGenerator.generateBuilding と同じ天井高の下限補正
        const floorHeight = floors >= 2 ? Math.max(5, configuredFloorHeight) : Math.max(3, configuredFloorHeight);

        const npcCells = collectNpcLocalCells(npcPoints, footprint.x, footprint.z, footprint.width, footprint.depth);
        const stairCells = new Set([
            ...collectBuildingStairCells(
                objectDef?.stairs ?? typeConfig.stairs,
                footprint.width,
                footprint.depth,
                floors
            ),
            ...npcCells
        ]);
        const groundCells = new Set(stairCells);
        for (const cell of collectBuildingDoorCells(objectDef, typeConfig, footprint.width, footprint.depth)) {
            groundCells.add(cell);
        }

        const chestCount = resolveInteriorChestCount(interiorWidth, interiorDepth, floors, interiorPolicy);
        const perFloor = distributeChestsToFloors(chestCount, floors, interiorPolicy.maxChestsPerFloor);
        const structureId = `building:${footprint.id}`;
        const pushPlacement = (cell, floorIndex, surfaceY) => {
            placements.push({
                x: clamp(footprint.x + cell.localX, 0, worldSize.width - 1),
                y: clamp(surfaceY, 0, MAX_BLOCK_Y),
                z: clamp(footprint.z + cell.localZ, 0, worldSize.depth - 1),
                score: 400,
                source: 'buildingInterior',
                structureId,
                floorIndex,
                floorCount: floors
            });
        };
        // 床ブロックの 1 つ上（プレイヤーが立つ高さ）
        const surfaceYOf = (floorIndex) => footprint.baseY + floorIndex * floorHeight + 1;

        let placedForBuilding = 0;
        for (const [floorIndex, floorChestCount] of perFloor) {
            const cells = pickInteriorCells(
                footprint.width,
                footprint.depth,
                floorIndex === 0 ? groundCells : stairCells,
                floorChestCount,
                interiorPolicy.minChestSeparation,
                `${meta.id}:${structureId}:${floorIndex}`
            );
            for (const cell of cells) pushPlacement(cell, floorIndex, surfaceYOf(floorIndex));
            placedForBuilding += cells.length;
        }

        // 小さな建物は内部が階段レーンやドア正面で埋まり、空きセルが無くなる。その場合でも
        // 「建造物内に必ず 1 個」を守るため、床が抜けない 1 階へ置く。1 階は床スラブが
        // 欠けないので、宝箱と同じ高さに乗る「最初の段」だけ避ければ床の上に立つ
        // （階段下の物置になる）。それでも空かなければドアだけ、最後は無条件で 1 個置く。
        if (placedForBuilding === 0) {
            const doorCells = collectBuildingDoorCells(objectDef, typeConfig, footprint.width, footprint.depth);
            const firstStepCells = collectGroundFlightFirstStepCells(
                objectDef?.stairs ?? typeConfig.stairs,
                footprint.width,
                footprint.depth,
                floors
            );
            const doorAndFirstStep = new Set([...doorCells, ...firstStepCells, ...npcCells]);
            for (const blockedCells of [doorAndFirstStep, firstStepCells, doorCells, new Set()]) {
                const fallbackCells = pickInteriorCells(
                    footprint.width,
                    footprint.depth,
                    blockedCells,
                    1,
                    interiorPolicy.minChestSeparation,
                    `${meta.id}:${structureId}:fallback`
                );
                if (fallbackCells.length === 0) continue;
                for (const cell of fallbackCells) pushPlacement(cell, 0, surfaceYOf(0));
                break;
            }
        }
    }

    return placements;
}

/**
 * 城の天守（mainKeep）内部の幾何。CastleGenerator.generateMainKeep と
 * resolveMainKeepFloorPlan / generateMainKeepSpiralStairs をそのまま再現する。
 */
function resolveCastleKeepGeometry(mapData, objectDef) {
    const config = objectDef?.config || {};
    if (config.enabled === false) return null;
    const keepConfig = config.mainKeep || {};

    const width = toPositiveIntOr(keepConfig.width, 18);
    const wallThickness = toPositiveIntOr(keepConfig.wallThickness, 2);
    const height = toPositiveIntOr(keepConfig.height, 58);
    const interiorHalf = width - wallThickness; // 床が張られる範囲 |dx| <= interiorHalf
    if (interiorHalf < 1) return null;

    const minCeilingClearance = Math.max(5, toPositiveIntOr(keepConfig.minCeilingClearance, 5));
    const floorInterval = Math.max(
        minCeilingClearance + 1,
        toPositiveIntOr(keepConfig.floorInterval, minCeilingClearance + 1)
    );
    const highestFloorY = Math.max(0, height - minCeilingClearance - 1);
    const floorLevels = [];
    for (let localY = 0; localY <= highestFloorY; localY += floorInterval) floorLevels.push(localY);

    const stairConfig = keepConfig.spiralStairs || {};
    const stairWidth = toPositiveIntOr(stairConfig.width, 2);
    const ringRadius = Math.max(2, Math.max(2, interiorHalf) - toNonNegativeIntOr(stairConfig.inset, 0));

    const reference = resolveObjectReferencePosition(mapData.meta, mapData.terrain, objectDef);
    const pos = Array.isArray(objectDef?.pos) ? objectDef.pos : [];
    const centerX = Math.floor(Number(reference.x) + Number(pos[0] || 0));
    const centerZ = Math.floor(Number(reference.z) + Number(pos[2] || 0));
    // MapObjectSpawner.resolvePosition: 城は明示 Y が無ければ地表ブロックの上（getGroundHeight）
    const explicitY = pos[1] !== undefined && Number(pos[1]) !== 0;
    const castleBaseY = explicitY
        ? Math.floor(Number(pos[1]))
        : getTerrainHeight(mapData.meta, mapData.terrain, centerX, centerZ) + 1;

    return {
        centerX,
        centerZ,
        keepBaseY: castleBaseY + 1, // generateMainKeep(centerX, baseY + 1, centerZ, height)
        interiorHalf,
        floorLevels,
        floorInterval,
        ringRadius,
        stairWidth
    };
}

/** 城（天守内部）の宝箱。上階ほど価値が上がる縦長の建造物なので階ごとに配る。 */
function buildCastleInteriorPlacements(mapData, interiorPolicy, npcPoints) {
    const { meta, objectsData } = mapData;
    const worldSize = getWorldSize(meta);
    const placements = [];
    const objects = Array.isArray(objectsData?.objects) ? objectsData.objects : [];

    for (const objectDef of objects) {
        if (objectDef?.type !== 'castle') continue;
        const keep = resolveCastleKeepGeometry(mapData, objectDef);
        if (!keep || keep.floorLevels.length === 0) continue;

        // 内寸を「壁 1 マス + 内部」の矩形に見立てて pickInteriorCells を再利用する
        const interiorSize = keep.interiorHalf * 2 + 1;
        const rectSize = interiorSize + 2;
        const originX = keep.centerX - keep.interiorHalf - 1;
        const originZ = keep.centerZ - keep.interiorHalf - 1;
        const blockedCells = collectNpcLocalCells(npcPoints, originX, originZ, rectSize, rectSize);
        for (let localX = 1; localX <= interiorSize; localX++) {
            for (let localZ = 1; localZ <= interiorSize; localZ++) {
                const dx = localX - 1 - keep.interiorHalf;
                const dz = localZ - 1 - keep.interiorHalf;
                const ringDistance = Math.max(Math.abs(dx), Math.abs(dz));
                // 螺旋階段のレーン（上階の床が抜けている）
                if (ringDistance > keep.ringRadius - keep.stairWidth && ringDistance <= keep.ringRadius) {
                    blockedCells.add(`${localX},${localZ}`);
                }
            }
        }

        const chestCount = resolveInteriorChestCount(
            interiorSize,
            interiorSize,
            keep.floorLevels.length,
            interiorPolicy
        );
        const perFloor = distributeChestsToFloors(chestCount, keep.floorLevels.length, interiorPolicy.maxChestsPerFloor);
        const structureId = `castle:${String(objectDef?.id || `${keep.centerX}:${keep.centerZ}`)}`;

        for (const [floorIndex, floorChestCount] of perFloor) {
            const cells = pickInteriorCells(
                rectSize,
                rectSize,
                blockedCells,
                floorChestCount,
                interiorPolicy.minChestSeparation,
                `${meta.id}:${structureId}:${floorIndex}`
            );
            // 城の基準 Y は地表ブロックの実測に依存するため、階の中ほど（床＋3）を
            // アンカーにして ±2 ブロックの誤差でも同じ階へ接地させる
            const anchorOffset = Math.max(1, Math.min(3, keep.floorInterval - 2));
            const surfaceY = keep.keepBaseY + keep.floorLevels[floorIndex] + anchorOffset;
            for (const cell of cells) {
                placements.push({
                    x: clamp(keep.centerX - keep.interiorHalf + cell.localX - 1, 0, worldSize.width - 1),
                    y: clamp(surfaceY, 0, MAX_BLOCK_Y),
                    z: clamp(keep.centerZ - keep.interiorHalf + cell.localZ - 1, 0, worldSize.depth - 1),
                    score: 420,
                    source: 'castleInterior',
                    structureId,
                    floorIndex,
                    floorCount: keep.floorLevels.length
                });
            }
        }
    }

    return placements;
}

/**
 * 塔ランドマーク（中空の circle シェル blockFill）の内部。
 * 階段が無い一本の吹き抜けなので、入口から入れる最下部だけに置く。
 */
function buildHollowShaftPlacements(mapData, interiorPolicy, npcPoints) {
    const { meta, terrain, objectsData } = mapData;
    const worldSize = getWorldSize(meta);
    const fills = Array.isArray(objectsData?.blockFills) ? objectsData.blockFills : [];
    const shafts = new Map();

    for (const fill of fills) {
        if (String(fill?.shape || '') !== 'circle') continue;
        if (normalizeBlockTypeId(fill?.innerBlockType) !== 'AIR') continue;
        if (normalizeBlockTypeId(fill?.blockType) === 'AIR') continue;
        const radius = Math.floor(Number(fill?.radius));
        if (!Number.isFinite(radius) || radius <= 0) continue;
        const shellThickness = Math.max(1, toPositiveIntOr(fill?.shellThickness, 1));
        const interiorRadius = radius - shellThickness;
        if (interiorRadius < 1) continue;

        const pos = Array.isArray(fill?.pos) ? fill.pos : [];
        const reference = resolveReferencePosition(meta, terrain, fill?.posRelativeTo || 'world_center');
        const centerX = Math.floor(Number(reference.x) + Number(pos[0] || 0));
        const centerZ = Math.floor(Number(reference.z) + Number(pos[2] || 0));
        const baseY = Math.floor(Number(pos[1]) || 0);
        const key = `${centerX},${centerZ}`;
        const current = shafts.get(key);
        // 同じ塔の複数セクションは、床（土台の上）がある最下段だけを使う
        if (!current || baseY < current.baseY) {
            shafts.set(key, { centerX, centerZ, baseY, interiorRadius, id: String(fill?.id || key) });
        }
    }

    const placements = [];
    for (const shaft of shafts.values()) {
        const interiorSize = shaft.interiorRadius * 2 + 1;
        const rectSize = interiorSize + 2;
        const blockedCells = collectNpcLocalCells(
            npcPoints,
            shaft.centerX - shaft.interiorRadius - 1,
            shaft.centerZ - shaft.interiorRadius - 1,
            rectSize,
            rectSize
        );
        for (let localX = 1; localX <= interiorSize; localX++) {
            for (let localZ = 1; localZ <= interiorSize; localZ++) {
                const dx = localX - 1 - shaft.interiorRadius;
                const dz = localZ - 1 - shaft.interiorRadius;
                if (dx * dx + dz * dz > shaft.interiorRadius * shaft.interiorRadius) {
                    blockedCells.add(`${localX},${localZ}`);
                }
            }
        }

        const chestCount = resolveInteriorChestCount(interiorSize, interiorSize, 1, interiorPolicy);
        const structureId = `shaft:${shaft.id}`;
        const cells = pickInteriorCells(
            rectSize,
            rectSize,
            blockedCells,
            chestCount,
            interiorPolicy.minChestSeparation,
            `${meta.id}:${structureId}`
        );
        for (const cell of cells) {
            placements.push({
                x: clamp(shaft.centerX - shaft.interiorRadius + cell.localX - 1, 0, worldSize.width - 1),
                y: clamp(shaft.baseY, 0, MAX_BLOCK_Y),
                z: clamp(shaft.centerZ - shaft.interiorRadius + cell.localZ - 1, 0, worldSize.depth - 1),
                score: 380,
                source: 'shaftInterior',
                structureId,
                floorIndex: 0,
                floorCount: 1
            });
        }
    }

    return placements;
}

/** 建造物内の宝箱をすべて集める（同一座標は 1 件へ畳む）。 */
function buildStructureInteriorPlacements(mapData, interiorPolicy) {
    if (!interiorPolicy.enabled) return [];
    const npcPoints = resolveNpcPoints(mapData);
    const placements = [
        ...buildBuildingInteriorPlacements(mapData, interiorPolicy, npcPoints),
        ...buildCastleInteriorPlacements(mapData, interiorPolicy, npcPoints),
        ...buildHollowShaftPlacements(mapData, interiorPolicy, npcPoints)
    ];

    const seen = new Set();
    return placements.filter((placement) => {
        const key = candidateKey(placement.x, placement.y, placement.z);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function buildEnemyCandidates(mapData, rng, options = {}) {
    const { meta, terrain, objectsData, enemies } = mapData;
    const worldSize = getWorldSize(meta);
    const onlyFloor = Number.isFinite(Number(options.onlyFloor)) ? Number(options.onlyFloor) : null;
    const onlySurfaceY = Number.isFinite(Number(options.onlySurfaceY)) ? Number(options.onlySurfaceY) : null;
    const enemyPositions = [];

    for (const enemyDef of enemies) {
        const entityPos = resolveEntityPosition(meta, terrain, enemyDef);
        if (!entityPos) continue;
        const anchorSurfaceY = resolveSurfaceY(meta, terrain, objectsData, entityPos.x, entityPos.z, entityPos.y);
        if (onlyFloor != null && getNearestDungeonFloor(terrain, anchorSurfaceY) !== onlyFloor) continue;
        if (onlySurfaceY != null && anchorSurfaceY !== onlySurfaceY) continue;
        enemyPositions.push({
            x: entityPos.x,
            y: anchorSurfaceY,
            z: entityPos.z
        });
    }

    const clusterRadius = Math.max(24, Math.floor(Number(options.clusterRadius) || (
        String(terrain?.generator || '') === 'dungeon' ? 72 : 120
    )));
    const clusters = clusterPoints(enemyPositions, clusterRadius);
    const results = [];

    for (const cluster of clusters) {
        const anchorRadius = Math.max(8, Math.min(18, 8 + Math.floor(cluster.size * 1.5)));
        const baseScore = 340 + cluster.size * 12 + rng() * 4;
        const sourceGroupId = `enemyCluster:${cluster.id}:${cluster.centerX}:${cluster.centerZ}`;

        for (let index = 0; index < ENEMY_CLUSTER_ANCHOR_OFFSETS.length; index++) {
            const [dx, dz] = ENEMY_CLUSTER_ANCHOR_OFFSETS[index];
            const scaledX = cluster.centerX + Math.round(dx * (anchorRadius / 10));
            const scaledZ = cluster.centerZ + Math.round(dz * (anchorRadius / 10));
            const x = clamp(scaledX, 1, worldSize.width - 2);
            const z = clamp(scaledZ, 1, worldSize.depth - 2);
            if (isCandidateInsidePlacementExclusion(mapData, x, z)) continue;

            const y = resolveSurfaceY(meta, terrain, objectsData, x, z, cluster.centerY);
            const score = baseScore - index * 7;
            results.push(normalizeCandidate(meta, x, y, z, score, 'enemy', {
                nearEnemy: true,
                sourceGroupId,
                sourceGroupLimit: 1,
                clusterSize: cluster.size
            }));
        }
    }

    return results;
}

function buildAreaCandidates(mapData, rng, countPerArea = 10, options = {}) {
    const { meta, terrain, objectsData } = mapData;
    const worldSize = getWorldSize(meta);
    const onlyFloor = Number.isFinite(Number(options.onlyFloor)) ? Number(options.onlyFloor) : null;
    const onlySurfaceY = Number.isFinite(Number(options.onlySurfaceY)) ? Number(options.onlySurfaceY) : null;
    const results = [];
    const areas = Array.isArray(meta?.areas) ? meta.areas : [];
    // 階層指定がある地下迷宮では、その階の床面を高さヒントに使う
    const floorHint = onlyFloor != null && String(terrain?.generator || '') === 'dungeon'
        ? getDungeonFloorSurfaceY(terrain, onlyFloor)
        : null;

    for (const area of areas) {
        const priority = Number.isFinite(Number(area?.priority)) ? Number(area.priority) : 1;
        const baseWeight = resolveAreaTypeWeight(area) + priority * 8 - (isHazardArea(area) ? 35 : 0);
        const sampleCount = Math.max(4, Math.floor(countPerArea + priority * 0.5));

        for (let i = 0; i < sampleCount; i++) {
            const sampled = sampleAreaPosition(area, rng, worldSize);
            if (!sampled) continue;
            const yHint = toNearYHint(area?.minY) ?? floorHint;
            const y = resolveSurfaceY(meta, terrain, objectsData, sampled.x, sampled.z, yHint);
            if (onlyFloor != null && String(terrain?.generator || '') === 'dungeon' && getNearestDungeonFloor(terrain, y) !== onlyFloor) {
                continue;
            }
            if (onlySurfaceY != null && y !== onlySurfaceY) continue;
            if (isCandidateInsidePlacementExclusion(mapData, sampled.x, sampled.z)) continue;

            results.push(normalizeCandidate(meta, sampled.x, y, sampled.z, baseWeight - i * 0.4 + rng(), 'area', {
                areaId: area?.id || '',
                hazardArea: isHazardArea(area),
                sourceGroupId: area?.id ? `area:${area.id}` : null,
                sourceGroupLimit: 2
            }));
        }
    }

    return results;
}

function buildVoidFloorCandidates(mapData, rng, targetSurfaceY = null) {
    const { meta, terrain, objectsData } = mapData;
    const floorRects = resolveVoidFloorRects(meta, terrain, objectsData);
    const results = [];

    for (const rect of floorRects) {
        if (targetSurfaceY != null && rect.surfaceY !== targetSurfaceY) continue;
        const areaScore = Math.sqrt(rect.sizeX * rect.sizeZ);
        const idText = rect.id.toLowerCase();
        const idBonus = /(throne|corridor|antechamber|tier|retreat|altar)/.test(idText) ? 50 : 0;
        const sampleCount = clamp(Math.floor((rect.sizeX * rect.sizeZ) / 5000), 4, 16);
        const margin = 10;

        for (let i = 0; i < sampleCount; i++) {
            const minX = clamp(rect.minX + margin, 1, rect.maxX);
            const maxX = clamp(rect.maxX - margin, minX, rect.maxX);
            const minZ = clamp(rect.minZ + margin, 1, rect.maxZ);
            const maxZ = clamp(rect.maxZ - margin, minZ, rect.maxZ);
            const x = pickRandomInt(rng, minX, maxX);
            const z = pickRandomInt(rng, minZ, maxZ);
            if (isCandidateInsidePlacementExclusion(mapData, x, z)) continue;
            results.push(normalizeCandidate(meta, x, rect.surfaceY, z, 260 + areaScore + idBonus - i * 0.5 + rng(), 'platform', {
                rectId: rect.id,
                sourceGroupId: rect.id ? `platform:${rect.id}` : null,
                sourceGroupLimit: 2
            }));
        }
    }

    return results;
}

function buildFallbackCandidates(mapData, rng, totalCount, options = {}) {
    const { meta, terrain, objectsData } = mapData;
    const worldSize = getWorldSize(meta);
    const results = [];
    const onlyFloor = Number.isFinite(Number(options.onlyFloor)) ? Number(options.onlyFloor) : null;
    const onlySurfaceY = Number.isFinite(Number(options.onlySurfaceY)) ? Number(options.onlySurfaceY) : null;

    // 階層指定がある地下迷宮では、その階の床面を高さヒントに使う
    // （ヒント無しだと全階の候補が同じ 1 階へ寄ってしまう）
    const floorHint = onlyFloor != null && String(terrain?.generator || '') === 'dungeon'
        ? getDungeonFloorSurfaceY(terrain, onlyFloor)
        : null;

    for (let i = 0; i < totalCount * 20; i++) {
        const x = pickRandomInt(rng, 16, Math.max(16, worldSize.width - 17));
        const z = pickRandomInt(rng, 16, Math.max(16, worldSize.depth - 17));
        const y = resolveSurfaceY(meta, terrain, objectsData, x, z, onlySurfaceY ?? floorHint);
        if (onlyFloor != null && String(terrain?.generator || '') === 'dungeon' && getNearestDungeonFloor(terrain, y) !== onlyFloor) {
            continue;
        }
        if (onlySurfaceY != null && y !== onlySurfaceY) continue;
        if (isCandidateInsidePlacementExclusion(mapData, x, z)) continue;
        results.push(normalizeCandidate(meta, x, y, z, 20 + rng(), 'fallback'));
    }

    return results;
}

function selectTreasurePositions(candidates, requiredCount, rng, options = {}) {
    const selected = [];
    const usedKeys = new Set();
    const minDistance = Math.max(3, Number(options.minDistance) || 6);
    const cellSize = Math.max(minDistance * 3, Math.floor(Number(options.cellSize) || 0));
    const maxPerCell = Math.max(1, Math.floor(Number(options.maxPerCell) || 1));
    const groupCounts = new Map();
    const cellCounts = new Map();

    const sorted = [...candidates].sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return hashString(`${a.source}:${a.x}:${a.y}:${a.z}`) - hashString(`${b.source}:${b.x}:${b.y}:${b.z}`);
    });

    function getCellKey(candidate) {
        if (cellSize <= 0) return null;
        return `${Math.floor(candidate.x / cellSize)}:${Math.floor(candidate.z / cellSize)}`;
    }

    function isTooClose(candidate, distanceLimit) {
        for (const existing of selected) {
            const horizontalDistance = Math.hypot(existing.x - candidate.x, existing.z - candidate.z);
            const verticalDistance = Math.abs(existing.y - candidate.y);
            if (horizontalDistance < distanceLimit && verticalDistance <= 2) {
                return true;
            }
        }
        return false;
    }

    function exceedsGroupLimit(candidate) {
        if (!candidate?.sourceGroupId) return false;
        const limit = Math.max(1, Math.floor(Number(candidate.sourceGroupLimit) || 0));
        if (limit <= 0) return false;
        return (groupCounts.get(candidate.sourceGroupId) || 0) >= limit;
    }

    function exceedsCellLimit(candidate) {
        const cellKey = getCellKey(candidate);
        if (!cellKey) return false;
        return (cellCounts.get(cellKey) || 0) >= maxPerCell;
    }

    function addCandidate(candidate) {
        const key = candidateKey(candidate.x, candidate.y, candidate.z);
        usedKeys.add(key);
        selected.push(candidate);

        if (candidate?.sourceGroupId) {
            groupCounts.set(candidate.sourceGroupId, (groupCounts.get(candidate.sourceGroupId) || 0) + 1);
        }

        const cellKey = getCellKey(candidate);
        if (cellKey) {
            cellCounts.set(cellKey, (cellCounts.get(cellKey) || 0) + 1);
        }
    }

    const passes = [
        { distanceLimit: minDistance, enforceCellLimit: true },
        { distanceLimit: minDistance, enforceCellLimit: false },
        { distanceLimit: Math.max(3, minDistance - 2), enforceCellLimit: false }
    ];

    for (const pass of passes) {
        for (const candidate of sorted) {
            if (selected.length >= requiredCount) break;
            const key = candidateKey(candidate.x, candidate.y, candidate.z);
            if (usedKeys.has(key)) continue;
            if (exceedsGroupLimit(candidate)) continue;
            if (pass.enforceCellLimit && exceedsCellLimit(candidate)) continue;
            if (isTooClose(candidate, pass.distanceLimit)) continue;
            addCandidate(candidate);
        }
    }

    let fallbackAttempts = 0;
    const fallbackAttemptLimit = Math.max(sorted.length * 4, 64);
    while (selected.length < requiredCount && sorted.length > 0 && fallbackAttempts < fallbackAttemptLimit) {
        fallbackAttempts++;
        const candidate = sorted[pickRandomInt(rng, 0, sorted.length - 1)];
        const key = candidateKey(candidate.x, candidate.y, candidate.z);
        if (usedKeys.has(key)) continue;
        if (exceedsGroupLimit(candidate)) continue;
        addCandidate(candidate);
    }

    return selected.slice(0, requiredCount);
}

/**
 * 報酬アイテムをショップ購入価格で 3 帯へ分ける（2026-08-02 指示）。
 * 銅 = copperMaxBuyPrice 以下 / 金 = goldMinBuyPrice 以上 / 銀 = その間。
 * 帯が空のときは安い側の帯で代替し、空だった帯名を emptyTiers に残す
 * （＝その価値帯のアイテムを作品世界観に合わせて創作すべきサイン）。
 */
function buildRewardPools(rewardCatalog, policy) {
    const tiers = (policy && typeof policy.rewardTiers === 'object' && policy.rewardTiers) || {};
    const copperMaxBuyPrice = toPositiveIntOr(tiers.copperMaxBuyPrice, REWARD_TIER_DEFAULTS.copperMaxBuyPrice);
    const goldMinBuyPrice = Math.max(
        copperMaxBuyPrice + 1,
        toPositiveIntOr(tiers.goldMinBuyPrice, REWARD_TIER_DEFAULTS.goldMinBuyPrice)
    );

    const copperItems = rewardCatalog.filter((item) => Number(item.buyPrice) <= copperMaxBuyPrice);
    const goldItems = rewardCatalog.filter((item) => Number(item.buyPrice) >= goldMinBuyPrice);
    const silverItems = rewardCatalog.filter((item) => Number(item.buyPrice) > copperMaxBuyPrice
        && Number(item.buyPrice) < goldMinBuyPrice);

    const emptyTiers = [];
    if (copperItems.length === 0) emptyTiers.push('copper');
    if (silverItems.length === 0) emptyTiers.push('silver');
    if (goldItems.length === 0) emptyTiers.push('gold');

    const withFallback = (primary, ...fallbacks) => {
        if (primary.length > 0) return primary;
        for (const pool of fallbacks) {
            if (pool.length > 0) return pool;
        }
        return rewardCatalog;
    };

    return {
        policy,
        priceBands: { copperMaxBuyPrice, goldMinBuyPrice },
        emptyTiers,
        copperItems: withFallback(copperItems, silverItems, goldItems),
        silverItems: withFallback(silverItems, copperItems, goldItems),
        goldItems: withFallback(goldItems, silverItems, copperItems)
    };
}

function pickDistinctItems(pool, rng, count) {
    if (!Array.isArray(pool) || pool.length <= 0) return [];
    const available = [...pool];
    const picked = [];
    const target = clamp(count, 1, available.length);
    while (picked.length < target && available.length > 0) {
        const index = pickRandomInt(rng, 0, available.length - 1);
        picked.push(available.splice(index, 1)[0]);
    }
    return picked;
}

/**
 * 銀・金の宝箱は「どれも同じアイテム」にならないよう、使用回数の少ないものから選ぶ（2026-08-02 指示）。
 * 同数の候補はシード付き乱数で散らす。使用回数カウンタは全ワールドで共有するため、
 * ワールドをまたいでも特定アイテムへ偏らない（プールが足りない場合だけ 2 巡目に入る）。
 */
function pickLeastUsedItems(pool, rng, count, usage) {
    if (!Array.isArray(pool) || pool.length <= 0) return [];
    const remaining = [...pool];
    const picked = [];
    const target = clamp(count, 1, remaining.length);

    while (picked.length < target && remaining.length > 0) {
        let fewestUses = Number.POSITIVE_INFINITY;
        for (const item of remaining) {
            fewestUses = Math.min(fewestUses, usage.get(item.itemId) || 0);
        }
        const leastUsed = remaining.filter((item) => (usage.get(item.itemId) || 0) === fewestUses);
        const chosen = leastUsed[pickRandomInt(rng, 0, leastUsed.length - 1)];
        picked.push(chosen);
        usage.set(chosen.itemId, fewestUses + 1);
        remaining.splice(remaining.indexOf(chosen), 1);
    }

    return picked;
}

/** 中身の重複を避けるランク一覧（既定は銀・金）。 */
function resolveDistinctRewardTiers(policy) {
    const raw = policy?.distinctRewardTiers;
    if (!Array.isArray(raw)) return DISTINCT_REWARD_TIER_DEFAULTS;
    const tiers = raw.map((value) => String(value || '').trim()).filter((value) => TREASURE_TYPES.includes(value));
    return tiers.length > 0 ? tiers : DISTINCT_REWARD_TIER_DEFAULTS;
}

/** 同一アイテムを複数個入れてよいランク（既定は金だけ）。 */
function resolveStackableRewardTiers(policy) {
    const raw = policy?.stackableRewardTiers;
    if (!Array.isArray(raw)) return STACKABLE_REWARD_TIER_DEFAULTS;
    const tiers = raw.map((value) => String(value || '').trim()).filter((value) => TREASURE_TYPES.includes(value));
    return tiers.length > 0 ? tiers : STACKABLE_REWARD_TIER_DEFAULTS;
}

/**
 * 1 種類あたりの個数。基本は 1 個で、「〇〇 x n」のまとめ入りは金の宝箱だけ（2026-08-02 指示）。
 * 金でも高額品は 1 個に留める。
 */
function resolveItemCountForDrop(item, rng, treasureType, stackableTiers) {
    if (!stackableTiers.includes(treasureType)) return 1;
    const price = Number(item?.buyPrice) || 0;
    if (price <= 30000) return pickRandomInt(rng, 1, 2);
    return 1;
}

function createRewardDefinition(treasureType, rewardPools, rng, rewardUsage = null) {
    const fallbackRanges = {
        copper: { min: 1, max: 2 },
        silver: { min: 2, max: 3 },
        gold: { min: 3, max: 4 }
    };
    const configured = rewardPools?.policy?.distinctItemCountByTreasureType?.[treasureType] || {};
    const fallback = fallbackRanges[treasureType] || fallbackRanges.copper;
    const minItems = Math.max(1, Math.floor(Number(configured.min) || fallback.min));
    const maxItems = Math.max(minItems, Math.floor(Number(configured.max) || fallback.max));
    const pool = rewardPools?.[`${treasureType}Items`] || rewardPools?.copperItems || [];
    const itemCount = pickRandomInt(rng, minItems, maxItems);
    // 銀・金は「同じアイテムばかり」を避けて選ぶ。銅は安価品前提でそのまま乱択（重複可）
    const avoidRepeats = rewardUsage instanceof Map
        && resolveDistinctRewardTiers(rewardPools?.policy).includes(treasureType);
    const picked = avoidRepeats
        ? pickLeastUsedItems(pool, rng, itemCount, rewardUsage)
        : pickDistinctItems(pool, rng, itemCount);
    const stackableTiers = resolveStackableRewardTiers(rewardPools?.policy);
    const itemDrops = picked.map((item) => ({
        itemId: item.itemId,
        count: resolveItemCountForDrop(item, rng, treasureType, stackableTiers)
    }));
    return { itemDrops };
}

function createRespawnHours(treasureType, rng) {
    if (treasureType === 'copper') {
        return [0, 1, 2, 3, 6, 12][pickRandomInt(rng, 0, 5)];
    }
    if (treasureType === 'silver') {
        return [0, 4, 8, 12, 24, 36][pickRandomInt(rng, 0, 5)];
    }
    return [0, 12, 24, 48, 72][pickRandomInt(rng, 0, 4)];
}

/**
 * 価値スコアの計算器を作る。スポーンからの水平距離と、スポーンより高い位置の高さを、
 * それぞれ「この配置群の中の最大値」で正規化して重み付き合計する。
 * ワールドの広さに依らず、塔や天守の上階が世界の果てと同格に評価される。
 */
function createValueScorer(positions, spawn, policy) {
    const ranking = (policy && typeof policy.valueRanking === 'object' && policy.valueRanking) || {};
    const distanceWeight = Number.isFinite(Number(ranking.distanceWeight))
        ? Number(ranking.distanceWeight)
        : VALUE_RANKING_DEFAULTS.distanceWeight;
    const heightWeight = Number.isFinite(Number(ranking.heightWeight))
        ? Number(ranking.heightWeight)
        : VALUE_RANKING_DEFAULTS.heightWeight;

    const horizontalOf = (position) => Math.hypot(position.x - spawn.x, position.z - spawn.z);
    const verticalOf = (position) => Math.max(0, position.y - spawn.y);
    let maxHorizontal = 0;
    let maxVertical = 0;
    for (const position of positions) {
        maxHorizontal = Math.max(maxHorizontal, horizontalOf(position));
        maxVertical = Math.max(maxVertical, verticalOf(position));
    }

    return (position) => (horizontalOf(position) / Math.max(1, maxHorizontal)) * distanceWeight
        + (verticalOf(position) / Math.max(1, maxVertical)) * heightWeight;
}

/** ワールド単位の宝箱ランク比率（既定 銅80% / 銀15% / 金5%）。合計が 1 でなくても正規化する。 */
function resolveTypeRatios(policy) {
    const raw = (policy && typeof policy.treasureTypeRatios === 'object' && policy.treasureTypeRatios) || {};
    const ratios = {};
    let total = 0;
    for (const treasureType of TREASURE_TYPES) {
        const value = Number(raw[treasureType]);
        ratios[treasureType] = Number.isFinite(value) && value >= 0
            ? value
            : TREASURE_TYPE_RATIO_DEFAULTS[treasureType];
        total += ratios[treasureType];
    }
    if (total <= 0) return { ...TREASURE_TYPE_RATIO_DEFAULTS };
    for (const treasureType of TREASURE_TYPES) ratios[treasureType] /= total;
    return ratios;
}

/** 全体件数から銅・銀・金の配分数を出す（端数は金へ寄せず銅→銀→金の順に丸める）。 */
function resolveTypeCounts(total, ratios) {
    const copper = clamp(Math.round(total * ratios.copper), 0, total);
    const silver = clamp(Math.round(total * ratios.silver), 0, total - copper);
    return { copper, silver, gold: total - copper - silver };
}

/**
 * 同じ建造物の中では上階ほどランクが高くなるよう補正する。
 * ワールド単位のランク比率（銅80% / 銀15% / 金5%）を崩さないため、
 * ランクの「総数」を変えない操作だけで行う。
 *
 * 1) 構造内のランクを階の昇順へ並べ替える（多重集合は不変＝総数不変）
 * 2) 複数階なのに全部同ランクの構造は、構造外でいちばん価値の低い同ランク上位の宝箱と
 *    ランクを交換して最上階を 1 段だけ上げる（＋1 と −1 を必ず対で行うので総数不変）
 *
 * @param {any[]} orderedByValue 価値スコア昇順に並んだ配置（交換相手の探索に使う）
 */
function enforceStructureFloorValueOrder(orderedByValue) {
    const groups = new Map();
    for (const position of orderedByValue) {
        if (!position.structureId) continue;
        if (!Number.isFinite(Number(position.floorIndex))) continue;
        if (!groups.has(position.structureId)) groups.set(position.structureId, []);
        groups.get(position.structureId).push(position);
    }

    const multiChestGroups = [...groups.values()].filter((group) => group.length >= 2);
    const structureMembers = new Set(multiChestGroups.flat());

    // 1) 構造内のランクを階順へ（総数は変えない）
    for (const group of multiChestGroups) {
        const byFloor = [...group].sort((a, b) => a.floorIndex - b.floorIndex);
        const ranks = byFloor
            .map((position) => TREASURE_TYPES.indexOf(position.treasureType))
            .sort((a, b) => a - b);
        byFloor.forEach((position, index) => {
            position.treasureType = TREASURE_TYPES[ranks[index]];
        });
    }

    // 2) 全部同ランクの複数階構造は、他の宝箱とランクを交換して最上階を 1 段上げる。
    //    交換相手（donor）は「構造に属さない宝箱」か「別構造の最下階の宝箱」に限る。
    //    どちらも 1 段下げても階順（非減少）が壊れないため、比率も階順も保てる。
    const groupOf = new Map();
    const lowestFloorMembers = new Set();
    for (const group of multiChestGroups) {
        const byFloor = [...group].sort((a, b) => a.floorIndex - b.floorIndex);
        lowestFloorMembers.add(byFloor[0]);
        for (const position of group) groupOf.set(position, group);
    }

    // 銀・金は総数が限られる（既定 20%）ため、全部同ランクの構造すべてを 1 段上げられるとは限らない。
    // 塔・高層建築のように階数の多い建造物から先に交換して、高さの演出を優先する。
    const promotionOrder = [...multiChestGroups].sort((a, b) => {
        const heightOf = (group) => Math.max(...group.map((position) => Number(position.floorIndex) || 0));
        return (heightOf(b) - heightOf(a)) || (b.length - a.length);
    });

    for (const group of promotionOrder) {
        const byFloor = [...group].sort((a, b) => a.floorIndex - b.floorIndex);
        const lowest = byFloor[0];
        const highest = byFloor[byFloor.length - 1];
        if (highest.floorIndex === lowest.floorIndex) continue;
        if (highest.treasureType !== lowest.treasureType) continue;

        const currentRank = TREASURE_TYPES.indexOf(highest.treasureType);
        if (currentRank >= TREASURE_TYPES.length - 1) continue;
        const donor = orderedByValue.find((position) => {
            if (TREASURE_TYPES.indexOf(position.treasureType) !== currentRank + 1) return false;
            if (!structureMembers.has(position)) return true;
            return groupOf.get(position) !== group && lowestFloorMembers.has(position);
        });
        if (!donor) continue;

        donor.treasureType = TREASURE_TYPES[currentRank];
        highest.treasureType = TREASURE_TYPES[currentRank + 1];
    }
}

/** 価値スコアの昇順に銅→銀→金を割り当て、建造物内の階順を補正する。 */
function assignTreasureTypes(positions, spawn, seedPrefix, policy) {
    const valueScoreOf = createValueScorer(positions, spawn, policy);
    const ordered = [...positions]
        .map((position) => ({ ...position }))
        .sort((a, b) => {
            const diff = valueScoreOf(a) - valueScoreOf(b);
            if (diff !== 0) return diff;
            return hashString(`${seedPrefix}:${a.x}:${a.y}:${a.z}`) - hashString(`${seedPrefix}:${b.x}:${b.y}:${b.z}`);
        });

    const counts = resolveTypeCounts(ordered.length, resolveTypeRatios(policy));
    let cursor = 0;
    for (const treasureType of TREASURE_TYPES) {
        for (let index = 0; index < counts[treasureType]; index++) {
            ordered[cursor].treasureType = treasureType;
            cursor += 1;
        }
    }

    enforceStructureFloorValueOrder(ordered);
    return ordered;
}

/**
 * 1 ワールド（マップ）分の宝箱を作る。
 * ランク配分はダンジョンの階層や空中要塞の段ごとではなく**マップ全体**で行い、
 * どのワールドでも 銅 80% / 銀 15% / 金 5% になるようにする（2026-08-02 指示）。
 * 採番・シードはバッチ（通常 / 階層 / 段）単位を維持して ID 体系を変えない。
 */
function createMapTreasures(positions, rewardPools, spawn, rewardUsage) {
    const typedPositions = assignTreasureTypes(positions, spawn, 'treasure-types', rewardPools?.policy);
    const batchCounters = new Map();

    const entries = typedPositions.map((position, order) => {
        const batch = position.batch;
        const indexInBatch = (batchCounters.get(batch.key) || 0) + 1;
        batchCounters.set(batch.key, indexInBatch);
        const rewardRng = createRng(
            `${batch.seedPrefix}:${position.treasureType}:${indexInBatch}:${position.x}:${position.y}:${position.z}`
        );
        return {
            batchOrder: batch.order,
            order,
            entry: {
                id: batch.buildId(position.treasureType, indexInBatch),
                treasureType: position.treasureType,
                pos: [position.x, position.y, position.z],
                reward: createRewardDefinition(position.treasureType, rewardPools, rewardRng, rewardUsage),
                respawnHours: createRespawnHours(position.treasureType, rewardRng)
            }
        };
    });

    // 出力はバッチ順（階層順）→ バッチ内の採番順に整える
    entries.sort((a, b) => (a.batchOrder - b.batchOrder) || (a.order - b.order));
    return entries.map((item) => item.entry);
}

/** 価値スコアの基準になる初期スポーン位置。 */
function resolveSpawnAnchor(mapData) {
    const { meta, terrain, objectsData } = mapData;
    const reference = resolveReferencePosition(meta, terrain, 'player_spawn');
    const rawY = Number(meta?.player?.spawnPos?.[1]);
    const y = Number.isFinite(rawY) && rawY !== 0
        ? Math.floor(rawY)
        : resolveSurfaceY(meta, terrain, objectsData, reference.x, reference.z, null);
    return { x: reference.x, y, z: reference.z };
}

/** 建造物内の宝箱を足した分だけ屋外の目標数を減らす（下限は残す）。 */
function resolveOutdoorCount(baseCount, minCount, interiorCount) {
    return Math.max(minCount, baseCount - interiorCount);
}

/** バッチ（採番・シードの単位）を作る。ランク配分はマップ全体で行うため位置に添えて運ぶ。 */
function createTreasureBatch(key, order, seedPrefix, buildId) {
    return { key, order, seedPrefix, buildId };
}

function withBatch(positions, batch) {
    return positions.map((position) => ({ ...position, batch }));
}

function buildRegularMapTreasures(mapData, rewardPools, interiorPlacements, rewardUsage) {
    const mapId = mapData.meta.id;
    const rng = createRng(`${mapId}:regular`);
    const outdoorCount = resolveOutdoorCount(
        OVERWORLD_OUTDOOR_COUNT,
        OVERWORLD_OUTDOOR_MIN_COUNT,
        interiorPlacements.length
    );
    const candidates = [
        ...buildBuildingCandidates(mapData, rng),
        ...buildEnemyCandidates(mapData, rng),
        ...buildAreaCandidates(mapData, rng, 10),
        ...buildFallbackCandidates(mapData, rng, 48)
    ];
    const selected = selectTreasurePositions(
        candidates,
        outdoorCount,
        rng,
        createSelectionOptions(mapData.meta, outdoorCount, 9, { maxPerCell: 2 })
    );
    const batch = createTreasureBatch(
        'regular',
        0,
        `${mapId}:regular`,
        (treasureType, index) => `${mapId}_${treasureType}_${String(index).padStart(3, '0')}`
    );
    return createMapTreasures(
        withBatch([...interiorPlacements, ...selected], batch),
        rewardPools,
        resolveSpawnAnchor(mapData),
        rewardUsage
    );
}

function buildDungeonMapTreasures(mapData, rewardPools, interiorPlacements, rewardUsage) {
    const mapId = mapData.meta.id;
    const terrain = mapData.terrain || {};
    const floorCount = Math.max(1, Math.floor(Number(terrain?.dungeon?.floorCount) || 10));
    const positions = [];

    for (let floor = 1; floor <= floorCount; floor++) {
        const rng = createRng(`${mapId}:floor:${floor}`);
        const floorInteriors = interiorPlacements.filter(
            (placement) => getNearestDungeonFloor(terrain, placement.y) === floor
        );
        const outdoorCount = resolveOutdoorCount(FLOOR_OUTDOOR_COUNT, FLOOR_OUTDOOR_MIN_COUNT, floorInteriors.length);
        const candidates = [
            ...buildBuildingCandidates(mapData, rng).filter((candidate) => getNearestDungeonFloor(terrain, candidate.y) === floor),
            ...buildEnemyCandidates(mapData, rng, { onlyFloor: floor }),
            ...buildAreaCandidates(mapData, rng, 9, { onlyFloor: floor }),
            ...buildFallbackCandidates(mapData, rng, 18, { onlyFloor: floor })
        ];
        const selected = selectTreasurePositions(
            candidates,
            outdoorCount,
            rng,
            createSelectionOptions(mapData.meta, outdoorCount, 7, { maxPerCell: 1 })
        );
        const floorLabel = String(floor).padStart(2, '0');
        const batch = createTreasureBatch(
            `floor:${floor}`,
            floor,
            `${mapId}:floor:${floor}`,
            (treasureType, index) => `${mapId}_f${floorLabel}_${treasureType}_${String(index).padStart(2, '0')}`
        );
        positions.push(...withBatch([...floorInteriors, ...selected], batch));
    }

    return createMapTreasures(positions, rewardPools, resolveSpawnAnchor(mapData), rewardUsage);
}

function buildCrystalArkTreasures(mapData, rewardPools, interiorPlacements, rewardUsage) {
    const mapId = mapData.meta.id;
    const floorRects = resolveVoidFloorRects(mapData.meta, mapData.terrain, mapData.objectsData)
        .filter((rect) => rect.sizeX >= 200 && rect.sizeZ >= 200)
        .sort((a, b) => b.surfaceY - a.surfaceY);
    const majorTierSurfaces = Array.from(new Set(floorRects.map((rect) => rect.surfaceY))).slice(0, 5);
    const positions = [];
    // 建造物内の宝箱は、いちばん高さの近い階層バッチへ入れる
    const nearestTierSurface = (y) => majorTierSurfaces.reduce(
        (best, surfaceY) => (Math.abs(surfaceY - y) < Math.abs(best - y) ? surfaceY : best),
        majorTierSurfaces[0]
    );

    for (const [tierIndex, surfaceY] of majorTierSurfaces.entries()) {
        const rng = createRng(`${mapId}:tier:${surfaceY}`);
        const tierInteriors = interiorPlacements.filter((placement) => nearestTierSurface(placement.y) === surfaceY);
        const outdoorCount = resolveOutdoorCount(FLOOR_OUTDOOR_COUNT, FLOOR_OUTDOOR_MIN_COUNT, tierInteriors.length);
        const candidates = [
            ...buildEnemyCandidates(mapData, rng, { onlySurfaceY: surfaceY }),
            ...buildVoidFloorCandidates(mapData, rng, surfaceY),
            ...buildAreaCandidates(mapData, rng, 9, { onlySurfaceY: surfaceY }),
            ...buildFallbackCandidates(mapData, rng, 18, { onlySurfaceY: surfaceY })
        ];
        const selected = selectTreasurePositions(
            candidates,
            outdoorCount,
            rng,
            createSelectionOptions(mapData.meta, outdoorCount, 8, { maxPerCell: 1 })
        );
        const tierLabel = String(surfaceY).padStart(2, '0');
        const batch = createTreasureBatch(
            `tier:${surfaceY}`,
            tierIndex,
            `${mapId}:tier:${surfaceY}`,
            (treasureType, index) => `${mapId}_y${tierLabel}_${treasureType}_${String(index).padStart(2, '0')}`
        );
        positions.push(...withBatch([...tierInteriors, ...selected], batch));
    }

    return createMapTreasures(positions, rewardPools, resolveSpawnAnchor(mapData), rewardUsage);
}

function loadMapData(mapId) {
    const mapDir = path.join(MAPS_DIR, mapId);
    return {
        mapDir,
        meta: readJson(path.join(mapDir, 'meta.json')),
        terrain: readJson(path.join(mapDir, 'terrain.json')),
        objectsData: fs.existsSync(path.join(mapDir, 'objects.json'))
            ? readJson(path.join(mapDir, 'objects.json'))
            : { objects: [], blockFills: [] },
        enemiesData: fs.existsSync(path.join(mapDir, 'enemies.json'))
            ? readJson(path.join(mapDir, 'enemies.json'))
            : { enemies: [] },
        npcsData: fs.existsSync(path.join(mapDir, 'npcs.json'))
            ? readJson(path.join(mapDir, 'npcs.json'))
            : { npcs: [] }
    };
}

function loadShopDocuments(mapIds, shopMaster) {
    const documents = [shopMaster];
    for (const mapId of mapIds) {
        const shopsPath = path.join(MAPS_DIR, mapId, 'shops.json');
        if (fs.existsSync(shopsPath)) documents.push(readJson(shopsPath));
    }
    return documents;
}

/**
 * 弾薬（effect=fire_equipped_weapon。物語武器の弾倉・ロケット弾など）は、
 * 各マップ（フィールド）の宝箱へ弾種ごとに最低 1 つ必ず入れる（2026-08-01 指示）。
 * ショップ以外のフィールド探索でも弾を回収でき、弾切れで装備武器が使えない詰みを防ぐ。
 * 入れる個数は shop-master.json の randomTreasurePolicy.ammoDropCount（既定 1 個）。
 * 配置先の宝箱はマップ ID＋弾薬 ID のシード付き乱数で決め、再生成しても同じ結果になる。
 * @returns 追加した弾薬の種類数
 */
function ensureAmmoDropGuarantee(mapId, treasures, ammoItems, treasurePolicy) {
    if (!Array.isArray(treasures) || treasures.length === 0) return 0;
    const dropCount = Math.max(1, Math.floor(Number(treasurePolicy?.ammoDropCount) || 1));
    let added = 0;
    for (const ammo of ammoItems) {
        const alreadyDropped = treasures.some((treasure) => (treasure?.reward?.itemDrops || [])
            .some((drop) => drop?.itemId === ammo.id));
        if (alreadyDropped) continue;
        const rng = createRng(`${mapId}:ammo:${ammo.id}`);
        const target = treasures[pickRandomInt(rng, 0, treasures.length - 1)];
        if (!target.reward || typeof target.reward !== 'object') target.reward = {};
        if (!Array.isArray(target.reward.itemDrops)) target.reward.itemDrops = [];
        target.reward.itemDrops.push({ itemId: ammo.id, count: dropCount });
        added += 1;
    }
    return added;
}

/**
 * 生成物の最終確認: 宝箱の中身が必ず抽選カタログ（＝ショップ販売品）に含まれること。
 * 宝物庫・ペット・配布専用・イベント限定の品が経路のどこかで紛れ込んだら、
 * 生成を失敗させて気付けるようにする（2026-08-02 指示の「徹底」）。
 */
function assertTreasureDropsAreShopItems(mapId, treasures, rewardCatalog) {
    const allowed = new Set(rewardCatalog.map((item) => item.itemId));
    const violations = new Set();
    for (const treasure of treasures) {
        for (const drop of treasure?.reward?.itemDrops || []) {
            const itemId = String(drop?.itemId || '').trim();
            if (!allowed.has(itemId)) violations.add(`${treasure.id}:${itemId}`);
        }
    }
    if (violations.size > 0) {
        throw new Error(
            `[generate-treasures] ${mapId}: ショップ販売品以外が宝箱に入っています → `
            + `${[...violations].join(', ')}`
        );
    }
}

function main() {
    const mapIds = readJson(MAP_LIST_PATH).maps || [];
    const shopMaster = readJson(SHOP_MASTER_PATH);
    const treasurePolicy = shopMaster.randomTreasurePolicy || {};
    const allItems = readJson(ITEMS_PATH).items || [];
    const itemCategories = fs.existsSync(ITEM_CATEGORIES_PATH) ? readJson(ITEM_CATEGORIES_PATH) : null;
    const rewardCatalog = buildRewardCatalog(
        allItems,
        loadShopDocuments(mapIds, shopMaster),
        treasurePolicy,
        itemCategories
    );
    if (rewardCatalog.length === 0) {
        throw new Error('ランダム宝箱に使用できる、ショップ購入価格上限内のアイテムがありません。');
    }
    // 弾薬の底上げも「ショップ販売品だけ」の原則に従う（未販売の弾は宝箱へ入れない）
    const catalogIds = new Set(rewardCatalog.map((item) => item.itemId));
    const ammoItems = allItems.filter((item) => String(item?.effect || '').trim() === 'fire_equipped_weapon'
        && catalogIds.has(String(item?.id || '').trim()));
    const interiorPolicy = resolveInteriorPolicy(treasurePolicy);
    const rewardPools = buildRewardPools(rewardCatalog, treasurePolicy);
    if (rewardPools.emptyTiers.length > 0) {
        // 価値帯が埋まっていないと、その宝箱ランクだけ別ランクと同じ中身になる。
        // 作品世界観に合うアイテムを該当価格帯で追加すること。
        console.warn(
            `[generate-treasures] 価値帯にアイテムがありません: ${rewardPools.emptyTiers.join(' / ')}`
            + `（銅=${rewardPools.priceBands.copperMaxBuyPrice}G以下 / 金=${rewardPools.priceBands.goldMinBuyPrice}G以上）。`
            + '該当価格帯のアイテムを作品の世界観に合わせて追加してください。'
        );
    }

    // 銀・金の中身が特定アイテムへ偏らないよう、使用回数は全ワールドで共有する
    const rewardUsage = new Map();

    for (const mapId of mapIds) {
        const mapData = loadMapData(mapId);
        mapData.enemies = Array.isArray(mapData.enemiesData?.enemies) ? mapData.enemiesData.enemies : [];
        const interiorPlacements = buildStructureInteriorPlacements(mapData, interiorPolicy);

        let treasures;
        if (mapId === 'labyrinth_dungeon' || String(mapData.terrain?.generator || '') === 'dungeon') {
            treasures = buildDungeonMapTreasures(mapData, rewardPools, interiorPlacements, rewardUsage);
        } else if (mapId === 'crystal_ark') {
            treasures = buildCrystalArkTreasures(mapData, rewardPools, interiorPlacements, rewardUsage);
        } else {
            treasures = buildRegularMapTreasures(mapData, rewardPools, interiorPlacements, rewardUsage);
        }
        ensureAmmoDropGuarantee(mapId, treasures, ammoItems, treasurePolicy);
        assertTreasureDropsAreShopItems(mapId, treasures, rewardCatalog);

        writeJson(path.join(mapData.mapDir, 'treasures.json'), {
            formatVersion: '1.0',
            mapId,
            treasures
        });
    }
}

main();
}
